/**
 * weaponLab.ts — Weapon Visual Test Lab
 * BETA 13.71
 *
 * Standalone testing tool exposed from the start menu. Lets the user:
 *   - Spawn a frozen player in an arena (no controls).
 *   - Toggle every weapon and item on/off, in any combination.
 *   - Spawn N enemies of any type at any time.
 *   - Take time-stamped notes that capture the active loadout, then export
 *     all notes as a CSV for review.
 *
 * The lab does NOT touch normal-run telemetry: no Bot Brain feed, no
 * sessionTracker entry, no goopState mutation, no run history.
 */

import { getWeaponSystemState } from './weaponAdapter';
import { equipWeapon, unequipWeapon, hasWeapon } from './weaponSystem';
import type { WeaponInstance, WeaponRarity } from './weapons';
import { WEAPON_DATABASE as WEAPONS } from './weapons';
import { getItemState } from './itemsAdapter';
import { addBullshiiii, removeBullshiiii } from './bunchaBullshiiiiSystem';
import { BUNCHA_BULLSHIIII } from './bunchaBullshiiii';
import { getEnemySystemState, debugSpawnEnemy } from './enemyAdapter';
import { ENEMY_DATABASE } from './enemies';
import { ultraChestPool, ULTRA_ITEMS } from './ultraItems';
import type { GameState } from './types';

// ─── Lab Mode Flag ────────────────────────────────────────────────────────────
let _labMode = false;
export function isLabMode(): boolean { return _labMode; }
export function setLabMode(v: boolean): void { _labMode = v; }

// BETA 14.05 — Lab "skip level-ups" toggle. When true, the engine skips the
// level-up choice screen entirely so the user can test a fixed loadout
// without UI interruption. Persisted across mounts via localStorage.
const LAB_SKIP_LEVELUP_KEY = 'goopzone_lab_skip_levelup_v1';
let _labSkipLevelUp = (() => {
  try { return localStorage.getItem(LAB_SKIP_LEVELUP_KEY) === '1'; } catch { return false; }
})();
export function isLabSkipLevelUp(): boolean { return _labSkipLevelUp; }
export function setLabSkipLevelUp(v: boolean): void {
  _labSkipLevelUp = v;
  try { localStorage.setItem(LAB_SKIP_LEVELUP_KEY, v ? '1' : '0'); } catch { /* ignore */ }
}

// BETA 14.07 — Calibrator-open flag. While the BarrelCalibrator modal is
// mounted, engine.ts skips its update() loop entirely so projectiles, enemies,
// timers all freeze. User: "if i am in this calibrator the game should pause".
// Set to true on mount, false on unmount, by BarrelCalibrator.tsx.
let _labCalibratorOpen = false;
export function isLabCalibratorOpen(): boolean { return _labCalibratorOpen; }
export function setLabCalibratorOpen(v: boolean): void { _labCalibratorOpen = v; }

// BETA 14.17 — Lab death log. Engine.ts intercepts player.hp ≤ 0 in lab mode
// and pushes a death event here (with timestamp + cause), then respawns the
// player at full HP. WeaponLab applyBuild() clears this log per build so the
// CSV row's deaths/deathCauses cells reflect ONLY deaths in that specific
// build's window. User: "should be in the csv file and such to track right?"
export interface LabDeathEvent { atSec: number; cause: string; }
let _labDeaths: LabDeathEvent[] = [];
/** Engine calls this each death; returns the new total count for UI display. */
export function pushLabDeath(ev: LabDeathEvent): number {
  _labDeaths.push(ev);
  return _labDeaths.length;
}
/** Snapshot at finalize time. */
export function getLabDeaths(): LabDeathEvent[] { return _labDeaths.slice(); }
/** Wipe the log. Called on applyBuild() so deaths attribute to the right build. */
export function clearLabDeaths(): void { _labDeaths = []; }

// BETA 14.27 — Per-build combat stats cache. Engine.ts pushes the live values
// of state.damageTakenThisRun + state._enemyAttacks* fields each frame via
// setCurrentCombatStats(); _buildRowSnapshot reads them and computes deltas
// against the snapshot taken at applyBuild() time. Avoids importing GameState
// directly into weaponLab (which would create a circular dep with engine.ts).
// User: per-build CSV needs damageTaken + enemyAttacksAttempted/Hit/Missed*
// to verify the BETA 14.23 no-contact-bubble fix is actually delivering hits.
interface CombatStats {
  damageTaken: number;
  attacksAttempted: number;
  attacksHit: number;
  attacksMissedByEvasion: number;
  attacksMissedByIframe: number;
  attacksMissedByPhase: number;
  // BETA 14.30 (BUG-05) — 5th outcome counter
  attacksMissedByOther: number;
  // BETA 14.30 Phase 1 — event counters
  reviveEvents: number;
  healEvents: number;
  dodgeEvents: number;
  phaseEvents: number;
  knockbackEvents: number;
}
let _cachedCombatStats: CombatStats = {
  damageTaken: 0,
  attacksAttempted: 0,
  attacksHit: 0,
  attacksMissedByEvasion: 0,
  attacksMissedByIframe: 0,
  attacksMissedByPhase: 0,
  attacksMissedByOther: 0,
  reviveEvents: 0,
  healEvents: 0,
  dodgeEvents: 0,
  phaseEvents: 0,
  knockbackEvents: 0,
};
export function setCurrentCombatStats(s: Partial<CombatStats>): void {
  _cachedCombatStats = { ..._cachedCombatStats, ...s };
}

// BETA 14.30 Phase 1 — final stat snapshot cache. Engine pushes per-frame
// values; weaponLab reads at finalize. Captures the player's end-of-frame
// stat values so the WL CSV row reflects build state at finalize time.
interface StatSnapshot {
  finalDamagePct: number; finalCritChancePct: number; finalCritDamagePct: number;
  finalAttackSpeedPct: number; finalMovementSpeedPct: number; finalLifestealPct: number;
  finalEvasionPct: number; finalPhaseChancePct: number; finalPickupRangePct: number;
  finalLockOnRange: number; finalKnockbackPct: number; finalEliteDamagePct: number;
  finalThornsPct: number; finalXPGainPct: number; finalGoldGainPct: number;
  finalMaxHP: number; finalHPRegen: number; finalArmor: number; finalShield: number;
  finalOverheal: number; finalProjectileCount: number; finalProjectileBounce: number;
  finalProjectilePierce: number;
}
let _cachedStatSnapshot: StatSnapshot = {
  finalDamagePct: 0, finalCritChancePct: 0, finalCritDamagePct: 0,
  finalAttackSpeedPct: 0, finalMovementSpeedPct: 0, finalLifestealPct: 0,
  finalEvasionPct: 0, finalPhaseChancePct: 0, finalPickupRangePct: 0,
  finalLockOnRange: 0, finalKnockbackPct: 0, finalEliteDamagePct: 0,
  finalThornsPct: 0, finalXPGainPct: 0, finalGoldGainPct: 0,
  finalMaxHP: 0, finalHPRegen: 0, finalArmor: 0, finalShield: 0,
  finalOverheal: 0, finalProjectileCount: 0, finalProjectileBounce: 0,
  finalProjectilePierce: 0,
};
export function setCurrentStatSnapshot(s: Partial<StatSnapshot>): void {
  _cachedStatSnapshot = { ..._cachedStatSnapshot, ...s };
}

// BETA 14.31 Phase 2 — module-level trackers for percs / items / shrines.
// Updated by incrementPercLevel / setItemStacks / applyShrineBuffById hooks
// below. _buildRowSnapshot reads these and emits as JSON cols. State on
// GameState (state._percTrackerThisRun etc.) is also populated for future
// BotFarm consumption via the same data — see engine.ts copy logic.
type PercTrackerEntry = { applies: number; currentStack: number; conditionMetFrames?: number; currentEffectiveBonus?: number; procCount?: number };
type ItemTrackerEntry = { applies: number; currentStack: number; procCount?: number; dmgDealt?: number; statusFramesAttributed?: number; qPressCount?: number; qReadyFrames?: number };
type ShrineTrackerEntry = { applies: number; totalDelta: number };
let _localPercTracker: Record<string, PercTrackerEntry> = {};
let _localItemTracker: Record<string, ItemTrackerEntry> = {};
let _localShrineTracker: Record<string, ShrineTrackerEntry> = {};
/** BETA 14.31 — call this when a perc level is applied. */
export function trackPercApply(percId: string, newStack: number): void {
  const e = _localPercTracker[percId] ?? { applies: 0, currentStack: 0 };
  e.applies += 1;
  e.currentStack = newStack;
  _localPercTracker[percId] = e;
}
/** BETA 14.58 — increment a perc's procCount (reactive/probabilistic procs).
 *  Used by N13 Not Xanax (post-damage timer fires) and N14 Not Acid (phase
 *  chance triggers on contact damage). Auto-creates entry if missing. */
export function trackPercProc(percId: string): void {
  const e = _localPercTracker[percId] ?? { applies: 0, currentStack: 0 };
  e.procCount = (e.procCount ?? 0) + 1;
  _localPercTracker[percId] = e;
}
/** BETA 14.58 — increment a perc's conditionMetFrames (per-frame sweep).
 *  Used by N05 Bungulazine (5+ enemies in 150wu), N06 Let's Ride (no enemies
 *  in 300wu), N08 Not Ritalin (first-60s window), N11 Stompatine (vs named
 *  elite), N12 Mr Silly Pills (diagonal movement). Engine sweeps per frame. */
export function trackPercCondition(percId: string): void {
  const e = _localPercTracker[percId] ?? { applies: 0, currentStack: 0 };
  e.conditionMetFrames = (e.conditionMetFrames ?? 0) + 1;
  _localPercTracker[percId] = e;
}
/** BETA 14.58 — set a perc's currentEffectiveBonus (latest snapshot).
 *  Used by N04 Not Blue Chew (% missing HP scaling), N07 Puberty Blockers
 *  (per-minute scaling), N09 Greedy Tums (gold-held scaling), N10 Debt
 *  (gold-spent scaling). Engine pushes the live computed value periodically. */
export function trackPercEffectiveBonus(percId: string, bonus: number): void {
  const e = _localPercTracker[percId] ?? { applies: 0, currentStack: 0 };
  e.currentEffectiveBonus = bonus;
  _localPercTracker[percId] = e;
}
/** BETA 14.31 — call this when an item stack changes. */
export function trackItemStackChange(itemId: string, newStack: number): void {
  // BETA 14.36 — guard against creating entries on clear-to-zero. The
  // applyBuild() clear pass calls setItemStacks(itemId, 0) for every
  // currently-equipped item, which previously created phantom
  // {applies:0, currentStack:0} entries that leaked into subsequent
  // builds. Now we skip if there's no prior entry AND the new stack is 0.
  const existing = _localItemTracker[itemId];
  if (!existing && newStack === 0) return;
  const e = existing ?? { applies: 0, currentStack: 0 };
  if (newStack > e.currentStack) e.applies += 1; // count as a pick when stack grows
  e.currentStack = newStack;
  _localItemTracker[itemId] = e;
}
/** BETA 14.58 — increment an item's procCount.
 *  Used by R11 Diarrhea Gas (cloud spawn on damage), R12 Drac's Gas Mask
 *  (overheal on poisoned hit), Big Mac Sauce (25% burn on hit), Mystery
 *  Meat Stew (kill-roll), Stay Frosty (slow on hit), Iranian Bird (zap),
 *  Bungulators (melee tick), Sheeesh / Liar Deceiver (revive). */
export function trackItemProc(itemId: string): void {
  const e = _localItemTracker[itemId] ?? { applies: 0, currentStack: 0 };
  e.procCount = (e.procCount ?? 0) + 1;
  _localItemTracker[itemId] = e;
}
/** BETA 14.58 — accumulate dmgDealt by an item's effect. */
export function trackItemDmg(itemId: string, dmg: number): void {
  if (dmg <= 0) return;
  const e = _localItemTracker[itemId] ?? { applies: 0, currentStack: 0 };
  e.dmgDealt = (e.dmgDealt ?? 0) + dmg;
  _localItemTracker[itemId] = e;
}
/** BETA 14.58 — Q-press counter for active items. */
export function trackItemQPress(itemId: string): void {
  const e = _localItemTracker[itemId] ?? { applies: 0, currentStack: 0 };
  e.qPressCount = (e.qPressCount ?? 0) + 1;
  _localItemTracker[itemId] = e;
}
/** BETA 14.58 — accumulate frames where Q was ready (cooldown elapsed). */
export function trackItemQReady(itemId: string): void {
  const e = _localItemTracker[itemId] ?? { applies: 0, currentStack: 0 };
  e.qReadyFrames = (e.qReadyFrames ?? 0) + 1;
  _localItemTracker[itemId] = e;
}

// BETA 14.59 — Last-player-damage-source cache. enemyAdapter sets this right
// before player.damage(). engine.ts HP-drop watcher consumes it as the
// fatalHitSource for deathContext + last5HitsBeforeDeath. Cleared on consume
// so multi-hit-same-frame still works (last source wins).
let _lastDamageSourceForFrame: string | null = null;
export function setLastDamageSource(s: string): void { _lastDamageSourceForFrame = s; }
export function consumeLastDamageSource(): string {
  const s = _lastDamageSourceForFrame ?? 'unknown';
  _lastDamageSourceForFrame = null;
  return s;
}
// BETA 16.90 — Per PSBOT cycle-30l §3 / cycle-30k Phase L4: needed to
// inspect the source mid-damage-pipeline (e.g. Bully Spray applies -50%
// damage from brawler-tagged enemies) without consuming it,
// so the engine's death-watcher can still consume it later.
export function peekLastDamageSource(): string | null {
  return _lastDamageSourceForFrame;
}

// BETA 14.63 — Current run-time cache. Engine.ts pushes state.time each frame
// so other modules (e.g., itemsAdapter shrine-select callback) can timestamp
// events without needing a state reference. Was the last reason shrine events
// in eventLog had t=0 — now they get the actual game-clock time.
let _currentRunTime = 0;
export function setCurrentRunTime(t: number): void { _currentRunTime = t; }
export function getCurrentRunTime(): number { return _currentRunTime; }

// ═══════════════════════════════════════════════════════════════════════════
// BETA 14.72 — BOT DIAGNOSTICS
// ═══════════════════════════════════════════════════════════════════════════
// User: "in the csv are we tracking the bots movements and such and location
// on map where it is and what is around it and possibilities to why its
// getting stuck and also have a way to make the bot better from the
// information you get?" Six new diagnostic streams added so PSBOT RUN can
// recommend specific bot AI tweaks from the data:
//
// 1. Position trail — sampled every 5s of game-time. Drives heatmap of where
//    bots die. Aggregate across runs → identify death traps (specific rooms,
//    coordinates, doorway approach angles).
//
// 2. Stuck event log — when a "stuck" warning fires, snapshot full context:
//    position, HP, level, nearby enemy count + types, weapon equipped, was
//    there a target in range / line of sight, categorized cause.
//
// 3. Map cell visits — heatmap-style cell-occupancy counter. 64×64 grid over
//    the world (256 cells). Shows which rooms / corridors the bot uses most.
//
// 4. Decision trace — bot brain state (flee/heal/kite weights) snapshot at
//    each major decision event so PSBOT RUN can see if weights fired correctly.
//
// 5. Surrounding context — at death + at every stuck event, capture enemy
//    types/distances within 200wu. Shows what was actually killing the bot.
//
// 6. LoS-to-enemy ratio — at stuck events, was there at least one enemy with
//    clear line of sight? If yes, bot AI is the problem (couldn't engage).
//    If no, pathfinding / wall is the problem.

type BotPositionSample = {
  t: number; x: number; y: number;
  hp: number; level: number; kills: number; enemyCount: number;
};
let _localBotPositionTrail: BotPositionSample[] = [];
const BOT_POS_SAMPLE_INTERVAL_SEC = 5.0;
let _lastBotPosSampleT = -1;
const BOT_POS_TRAIL_CAP = 500; // 500 samples × 5s = 2500s = ~42min coverage
export function trackBotPosition(t: number, x: number, y: number, hp: number, level: number, kills: number, enemyCount: number): void {
  // Gate to once-per-interval to avoid bloating CSV with per-frame data
  if (t - _lastBotPosSampleT < BOT_POS_SAMPLE_INTERVAL_SEC) return;
  _lastBotPosSampleT = t;
  if (_localBotPositionTrail.length >= BOT_POS_TRAIL_CAP) _localBotPositionTrail.shift();
  _localBotPositionTrail.push({ t, x, y, hp, level, kills, enemyCount });
}
export function getLocalBotPositionTrail(): BotPositionSample[] { return _localBotPositionTrail; }

type StuckEvent = {
  t: number; x: number; y: number;
  hp: number; hpFrac: number; level: number; kills: number;
  enemyCount: number;
  enemyTypesNearby: Record<string, number>; // type → count within 200wu
  hasWeaponInRange: boolean;
  hasLoSToNearestEnemy: boolean;
  nearestEnemyDist: number;
  category: 'enemy_ring' | 'wall_block' | 'iframe' | 'no_target' | 'weapon_oor' | 'flow_field_deadlock' | 'other';
  // BETA 14.76 — bot-fault diagnostic fields (per PSBOT RUN cycle-10 finding:
  // 84% of stuck events are bot-fault, not enemy-fault. These fields let the
  // analyst pass tell WHY the bot was stuck, not just WHEN.)
  /** What weapons the bot has equipped + their actual lock-on ranges. Critical
   *  for `no_target`/`weapon_oor` analysis — the previous heuristic used a
   *  blanket 300wu lock-on assumption which is wrong (spatula=65, sniper=600). */
  equippedWeapons?: Array<{ id: string; level: number; lockOnRange: number }>;
  /** Closest enemy within ANY equipped weapon's lock-on range (or null if none).
   *  If null with enemies present → bot positioning bug (`weapon_oor`). */
  closestEnemyInLockOnRange?: { type: string; dist: number; weaponId: string } | null;
  /** Bot's velocity at stuck moment. (0, 0) → bot is stationary (worst case);
   *  non-zero → bot is moving but not progressing (pathing bug). */
  botVelocity?: { vx: number; vy: number; speed: number };
  /** Top 8 closest enemies in 500wu — type, dist, angle from bot. Lets analyst
   *  see if enemies are clustered (one direction) vs surrounding bot. */
  nearbyEnemiesDetail?: Array<{ type: string; dist: number; angleDeg: number }>;
  /** Pickup orbs (XP / gold / health) within 500wu. If high + bot is stuck →
   *  bot couldn't reach a desired loot orb. */
  pickupOrbsNearby?: number;
  /** Seconds since the bot's last kill. High value = bot has been "engaging"
   *  without success for a long time. */
  secondsSinceLastKill?: number;
  /** Seconds since the bot dealt ANY damage. Even broader staleness indicator —
   *  high value with non-zero kills means bot just stops attacking after kills. */
  secondsSinceLastDamageDealt?: number;
};
let _localStuckEvents: StuckEvent[] = [];
const STUCK_EVENT_CAP = 100;
export function trackStuckEvent(ev: StuckEvent): void {
  if (_localStuckEvents.length >= STUCK_EVENT_CAP) _localStuckEvents.shift();
  _localStuckEvents.push(ev);
}
export function getLocalStuckEvents(): StuckEvent[] { return _localStuckEvents; }

// Map cell visits — coarse 64×64 grid over an 18000wu world = ~280wu per cell.
// Bigger than a room (~768wu) so each cell aggregates a small region.
let _localBotMapCellVisits: Record<string, number> = {};
const BOT_MAP_CELL_SIZE = 280;
let _lastMapCellSample = -1;
const MAP_CELL_SAMPLE_INTERVAL_SEC = 0.5; // 2× per second is enough for heatmap
export function trackBotMapCell(t: number, x: number, y: number): void {
  if (t - _lastMapCellSample < MAP_CELL_SAMPLE_INTERVAL_SEC) return;
  _lastMapCellSample = t;
  const cx = Math.floor(x / BOT_MAP_CELL_SIZE);
  const cy = Math.floor(y / BOT_MAP_CELL_SIZE);
  const key = `${cx},${cy}`;
  _localBotMapCellVisits[key] = (_localBotMapCellVisits[key] ?? 0) + 1;
}
export function getLocalBotMapCellVisits(): Record<string, number> { return _localBotMapCellVisits; }

type DecisionEvent = {
  t: number; type: string; // 'pick' | 'flee' | 'heal_seek' | 'kite' | 'engage'
  data: Record<string, number | string | boolean>;
};
let _localDecisionTrace: DecisionEvent[] = [];
const DECISION_TRACE_CAP = 200;
export function trackDecisionEvent(ev: DecisionEvent): void {
  if (_localDecisionTrace.length >= DECISION_TRACE_CAP) _localDecisionTrace.shift();
  _localDecisionTrace.push(ev);
}
export function getLocalDecisionTrace(): DecisionEvent[] { return _localDecisionTrace; }

// Reset all bot diagnostics on per-build clear (called from clearTrackers).
export function clearBotDiagnostics(): void {
  _localBotPositionTrail = [];
  _lastBotPosSampleT = -1;
  _localStuckEvents = [];
  _localBotMapCellVisits = {};
  _lastMapCellSample = -1;
  _localDecisionTrace = [];
}
/** BETA 14.58 — accumulate status frames attributed to an item (e.g.
 *  Whoopie Cushion stun frames, Stay Frosty slow frames per enemy). */
export function trackItemStatusFrames(itemId: string, frames: number): void {
  if (frames <= 0) return;
  const e = _localItemTracker[itemId] ?? { applies: 0, currentStack: 0 };
  e.statusFramesAttributed = (e.statusFramesAttributed ?? 0) + frames;
  _localItemTracker[itemId] = e;
}
/** BETA 14.31 — call this when a shrine buff is applied. */
export function trackShrineApply(shrineKey: string, delta: number): void {
  const e = _localShrineTracker[shrineKey] ?? { applies: 0, totalDelta: 0 };
  e.applies += 1;
  e.totalDelta += delta;
  _localShrineTracker[shrineKey] = e;
}
export function getLocalPercTracker(): Record<string, PercTrackerEntry> { return _localPercTracker; }
export function getLocalItemTracker(): Record<string, ItemTrackerEntry> { return _localItemTracker; }
export function getLocalShrineTracker(): Record<string, ShrineTrackerEntry> { return _localShrineTracker; }
export function clearTrackers(): void {
  _localPercTracker = {};
  _localItemTracker = {};
  _localShrineTracker = {};
  _localFloorEffectTracker = {};
  _localWeaponEnemyMatrix = {};
  _localSynergyTracker = {};
  _localEventLog = [];
  _localDeathContext = null;
  _localNearMissEvents = [];
  _localFirstBloodTime = 0;
  _localFirstDamageTakenTime = 0;
  _localLongestStretchBetweenHitsMs = 0;
  // BETA 14.54/14.56 — Reset Ugly Kittens local counters per build.
  _localKittenPlacements = 0;
  _localKittenAttacks = 0;
  _localKittenDmg = 0;
  _localKittenKills = 0;
  _localKittenLifetimesMs = 0;
  _localKittenLifetimeCount = 0;
  _localKittenCornerCount = 0;
  _localKittenCenterCount = 0;
  _localKittenScatterCount = 0;
  // BETA 14.36 — also reset framePerf + retreat events on per-build clear.
  // Previous bug: framePerf.spikes accumulated across all builds in session.
  clearFramePerf();
  clearRetreatEvents();
  // BETA 14.72 — Reset bot diagnostics on per-build clear.
  clearBotDiagnostics();
}

// BETA 14.34 Phase 5 — event log + death context + near-miss + timing + perf.
type EventLogEntry = { t: number; type: string; data: Record<string, unknown> };
let _localEventLog: EventLogEntry[] = [];
// BETA 14.66 — Cap raised from 200 → 2000. CSV evidence
// (weaponlab_session_1777613083742): 200 was getting saturated by kill +
// pickup events in heavy builds (~150-200/build), evicting the 15 shrine
// events that fire at applyBuild start. Shrines were pushed correctly but
// then drop-oldest eviction wiped them before CSV emit. 2000 covers typical
// kill counts (largest observed: 1158 kills / 17 builds = ~68/build × ~3
// safety margin = ~200/build) plus shrines + pickups + nearMiss without
// eviction. Per-build clearTrackers still resets so memory stays bounded.
const EVENT_LOG_CAP = 2000;
export function pushEventLog(entry: EventLogEntry): void {
  if (_localEventLog.length >= EVENT_LOG_CAP) _localEventLog.shift();
  _localEventLog.push(entry);
}
export function getLocalEventLog(): EventLogEntry[] { return _localEventLog; }

type DeathContext = {
  hpAtDeath: number; fatalHitSource: string; fatalHitDmg: number;
  last5HitsBeforeDeath: Array<{ t: number; source: string; dmg: number }>;
  distanceToNearestEnemy: number; enemiesInContactRangeAtDeath: number;
  iframeStateAtDeath: number;
  // BETA 22.48 — death clarity (see GameState.deathExplanation).
  fatalHitAgeSec?: number;          // gap between last HP-drop and the death frame (-1 = none)
  deathWasPhantom?: boolean;        // no fresh enemy hit at death → suspicious
};
let _localDeathContext: DeathContext | null = null;
export function setDeathContext(ctx: DeathContext): void { _localDeathContext = ctx; }
export function getLocalDeathContext(): DeathContext | null { return _localDeathContext; }

type NearMissEvent = {
  t: number; hpAtNadir: number; recoveryTime: number;
  recoverySource: string;
};
// BETA 14.51 — cap to prevent unbounded growth during long sessions. OOM
// crash hunt: this array had no cap; under heavy fire (20-min difficulty +
// max-weapons), near-miss detection can fire dozens of times per second,
// growing the array indefinitely. Drop-oldest behavior matches _localEventLog.
const NEAR_MISS_EVENT_CAP = 500;
let _localNearMissEvents: NearMissEvent[] = [];
export function pushNearMissEvent(ev: NearMissEvent): void {
  if (_localNearMissEvents.length >= NEAR_MISS_EVENT_CAP) _localNearMissEvents.shift();
  _localNearMissEvents.push(ev);
}
export function getLocalNearMissEvents(): NearMissEvent[] { return _localNearMissEvents; }

let _localFirstBloodTime = 0;
let _localFirstDamageTakenTime = 0;
let _localLongestStretchBetweenHitsMs = 0;
export function setFirstBloodTime(t: number): void { if (_localFirstBloodTime === 0) _localFirstBloodTime = t; }
export function setFirstDamageTakenTime(t: number): void { if (_localFirstDamageTakenTime === 0) _localFirstDamageTakenTime = t; }
export function setLongestStretchBetweenHitsMs(ms: number): void {
  if (ms > _localLongestStretchBetweenHitsMs) _localLongestStretchBetweenHitsMs = ms;
}
export function getLocalTimingBaselines(): { firstBlood: number; firstDamageTaken: number; longestStretch: number } {
  return {
    firstBlood: _localFirstBloodTime,
    firstDamageTaken: _localFirstDamageTakenTime,
    longestStretch: _localLongestStretchBetweenHitsMs,
  };
}

// Engine-pushed frame-perf summary
interface FramePerfSnapshot {
  spikes: Array<{ t: number; frameTimeMs: number }>;
  avgFrameTimeMs: number; worstFrameTimeMs: number;
}
let _cachedFramePerf: FramePerfSnapshot = {
  spikes: [], avgFrameTimeMs: 0, worstFrameTimeMs: 0,
};
export function setCurrentFramePerf(p: FramePerfSnapshot): void {
  _cachedFramePerf = p;
}
export function clearFramePerf(): void {
  _cachedFramePerf = { spikes: [], avgFrameTimeMs: 0, worstFrameTimeMs: 0 };
}

// BETA 14.36 — Retreat / warp event log per the user's request: "build in a
// tracker in weapons testing for the csv to track those that run away and
// where they spawn in and how close and how long it takes to get to me."
// Each retreat-and-warp produces a 2-stage event:
//   1. retreat-start: enemy enters retreat mode (peel off)
//   2. warp-resolve: at retreat-end, enemy is recycled to forward arc by
//      the next Flank Warp tick. Capture the new spawn position + distance
//      from player + enemy type.
//   3. (optional) re-contact: when the warped enemy is back within touchDist
//      of player, capture how long after warp it took.
export interface RetreatEvent {
  enemyType: string;
  enemyVariant: string;
  retreatStartT: number;     // state.time when retreat began
  retreatStartX: number;
  retreatStartY: number;
  warpEndT: number;          // state.time when retreat ended + warp fired
  warpEndX: number;
  warpEndY: number;
  warpDistFromPlayer: number; // distance to player at warp-end
  reContactT?: number;       // state.time when enemy reached touchDist of player again
  timeToContactSec?: number; // reContactT - warpEndT
}
let _localRetreatEvents: RetreatEvent[] = [];
const RETREAT_EVENT_CAP = 200;
export function pushRetreatStart(enemyType: string, enemyVariant: string, atT: number, x: number, y: number): number {
  if (_localRetreatEvents.length >= RETREAT_EVENT_CAP) return -1;
  _localRetreatEvents.push({
    enemyType, enemyVariant,
    retreatStartT: atT, retreatStartX: x, retreatStartY: y,
    warpEndT: 0, warpEndX: 0, warpEndY: 0, warpDistFromPlayer: 0,
  });
  return _localRetreatEvents.length - 1;
}
export function recordWarpEnd(eventIdx: number, atT: number, x: number, y: number, distFromPlayer: number): void {
  if (eventIdx < 0 || eventIdx >= _localRetreatEvents.length) return;
  _localRetreatEvents[eventIdx].warpEndT = atT;
  _localRetreatEvents[eventIdx].warpEndX = x;
  _localRetreatEvents[eventIdx].warpEndY = y;
  _localRetreatEvents[eventIdx].warpDistFromPlayer = distFromPlayer;
}
export function recordReContact(eventIdx: number, atT: number): void {
  if (eventIdx < 0 || eventIdx >= _localRetreatEvents.length) return;
  const ev = _localRetreatEvents[eventIdx];
  if (ev.reContactT !== undefined) return; // first re-contact only
  ev.reContactT = atT;
  ev.timeToContactSec = atT - ev.warpEndT;
}
export function getLocalRetreatEvents(): RetreatEvent[] { return _localRetreatEvents; }
export function clearRetreatEvents(): void { _localRetreatEvents = []; }

// BETA 14.38 — Retreat simulation knobs (live-tuned via WeaponLab sliders).
// Defaults match the BETA 14.37 values; user moves sliders to find good
// numbers empirically, then we can hardcode the winners.
interface RetreatTuning {
  trailingThresholdSec: number;   // Seconds of trailing before retreat eligible
  retreatProbPerSec: number;      // Per-second retreat probability after threshold
  retreatDurationSec: number;     // How long the peel-off lasts
  warpIntervalSec: number;        // Flank Warp tick interval
  spawnMarginWu: number;          // Min wu past viewport edge for spawn/warp
  spawnMaxDistWu: number;         // Max wu past viewport edge
  // BETA 14.40 — Warp count + angle randomness sliders
  warpCountPerTick: number;       // How many enemies warp per warp tick (1-50)
  warpForwardBiasPct: number;     // % of warps that bias toward player's forward arc (0=pure random angles, 100=all forward)
  // BETA 14.41 — Movement gate + attack-mode toggle. User: "im standing
  // still and enemies are running away... they should just be coming at
  // me." Required player velocity (wu/s) for trailingSince to accumulate.
  // 0 = retreats always fire (lab observation mode). 80 = retreats only
  // fire when player is meaningfully moving (real-game behavior).
  requiredPlayerVelMag: number;
  // BETA 14.41 — Attack-mode toggle. When true, retreat logic is bypassed
  // entirely — enemies just chase + attack. User: "these things are not
  // even attacking me. its too focused on retreating."
  attackModeOnly: boolean;
}
// BETA 14.40 — Defaults tuned for "best feel" per user: "default needs to
// be set to what you think the best is for the retreat simulation."
// Threshold 3.0 (gives engaged enemies time to fight before peeling off,
// no more "retreat from 2 inches away" complaint), prob 0.6 (slow rate
// so cycling is gradual), warp tick 3.0 (more frequent than 5.0 so the
// recycled enemies arrive without long gaps), warp count 8 (between old
// floor 3 and ceiling 20), forward bias 60% (matches existing 60/40 split).
// BETA 14.44 — Defaults locked in from user's empirical Retreat Sim tuning
// session (Apr 30 2026 dump). attackModeOnly stays TRUE (VS-classic chase
// + spawn stream). Three tuning values changed from BETA 14.42 defaults:
//   spawnMaxDistWu     200 → 320   (wider variety in spawn distance)
//   warpCountPerTick   8   → 50    (recycle ALL eligible per tick — max density)
//   warpForwardBiasPct 60  → 0     (pure 360° random — no forward bias)
// The trailing/prob/duration values stay at sane defaults since they're
// inactive while attackModeOnly is true; user can flip Attack Mode off
// in the sim panel to test retreat mechanics.
let _retreatTuning: RetreatTuning = {
  trailingThresholdSec: 3.0,
  retreatProbPerSec: 0.6,
  retreatDurationSec: 1.5,
  warpIntervalSec: 3.0,
  spawnMarginWu: 50,
  spawnMaxDistWu: 320,
  warpCountPerTick: 50,
  warpForwardBiasPct: 0,
  requiredPlayerVelMag: 80,
  attackModeOnly: true,
};
export function getRetreatTuning(): RetreatTuning { return _retreatTuning; }
export function setRetreatTuning(t: Partial<RetreatTuning>): void {
  _retreatTuning = { ..._retreatTuning, ...t };
}
export function resetRetreatTuning(): void {
  // BETA 14.44 — Defaults locked from user's empirical Retreat Sim session.
  _retreatTuning = {
    trailingThresholdSec: 3.0, retreatProbPerSec: 0.6, retreatDurationSec: 1.5,
    warpIntervalSec: 3.0, spawnMarginWu: 50, spawnMaxDistWu: 320,
    warpCountPerTick: 50, warpForwardBiasPct: 0,
    requiredPlayerVelMag: 80, attackModeOnly: true,
  };
}

// BETA 14.42 — Wave spawn rate multiplier. User wants "continuous" stream
// of enemies. The wave system already scales per-minute, but exposing a
// multiplier lets the user crank density up for testing. 1.0 = default,
// 5.0 = 5× normal spawn rate.
let _waveSpawnRateMult = 1.0;
export function getWaveSpawnRateMult(): number { return _waveSpawnRateMult; }
export function setWaveSpawnRateMult(v: number): void {
  _waveSpawnRateMult = Math.max(0.1, Math.min(10.0, v));
}

// BETA 14.45 — Per-build state reset request. weaponLab.applyBuild() can't
// import from engine.ts (circular dep), so it sets this flag and engine.ts
// reads + clears it next frame. Resets:
//  - state._mysteryMeatBonuses (per-run accumulator from item_mystery_meat_stew)
//  - state._runGoldSpent       (per-run gold spent on chests/shrines)
//  - state._postDamageTimer    (recently-damaged window)
//  - state._liarUsedThisRun    (Liar Deceiver one-shot revive flag)
//  - state._reviveEventsThisRun (BETA 14.30 counter)
//  - state._firstBloodTime / _firstDamageTakenTime / _longestStretchBetweenHitsMs
//  - engine module-level _sheeeshUsedThisRun (via resetReviveFlagsForBuild)
//  - state.lastStandAvailable (so each build gets a fresh Last Stand)
// User: "RANDOMIZE doesn't reset between rolls" — by 11th re-roll player has
// 9755 HP + 25888% damage from accumulated state.
let _perBuildResetRequested = false;
export function requestPerBuildStateReset(): void { _perBuildResetRequested = true; }
export function consumePerBuildStateReset(): boolean {
  if (!_perBuildResetRequested) return false;
  _perBuildResetRequested = false;
  return true;
}
// BETA 17.90 — Peek-without-consume helper. Used by engine.ts to detect a
// pending per-build reset BEFORE the phase-gated early return at line 1599
// fires. Without this, a build ending in 'won' / 'gameover' / 'levelup'
// caused subsequent builds to be permanently stuck because the engine
// bailed out before reaching the consumePerBuildStateReset() block.
export function isPerBuildStateResetPending(): boolean { return _perBuildResetRequested; }

// BETA 14.38 — Visual debug overlay flags (controlled by retreat sim panel).
let _retreatSimShowFovBox = false;
let _retreatSimZoom = 1.0;
export function getRetreatSimShowFovBox(): boolean { return _retreatSimShowFovBox; }
export function setRetreatSimShowFovBox(v: boolean): void { _retreatSimShowFovBox = v; }
export function getRetreatSimZoom(): number { return _retreatSimZoom; }
export function setRetreatSimZoom(v: number): void { _retreatSimZoom = Math.max(0.25, Math.min(2.0, v)); }

// BETA 14.38 — Retreat sim spawn helper. Spawns N enemies in an arc at the
// requested angle (degrees, 0 = east, 90 = south, etc.) and distance from
// the player. Even-distributed across `arcSpanDeg` degrees. (debugSpawnEnemy
// already imported at the top of this file.)
export function spawnEnemiesInArc(opts: {
  count: number;
  centerAngleDeg: number;     // 0 = east, 90 = south, 180 = west, 270 = north
  arcSpanDeg: number;          // total degrees to spread across (e.g., 60° = tight cluster, 360° = full ring)
  distanceFromPlayerWu: number;
  enemyDefinitionId?: string;  // defaults to 'zombie'
}): number {
  const ws = getWeaponSystemState();
  if (!ws) return 0;
  // Get player position from weapon system slot (proxy — every weapon orbits player so slot center ≈ player).
  // Better: read from engine. Just use a global accessor from window if available; fallback to hardcoded center.
  const _state = (window as unknown as { __getGameState?: () => { player: { x: number; y: number } } }).__getGameState?.();
  const px = _state?.player.x ?? 9000;  // fallback to roughly world center
  const py = _state?.player.y ?? 9000;
  const def = opts.enemyDefinitionId ?? 'zombie';
  const span = (opts.arcSpanDeg * Math.PI) / 180;
  const center = (opts.centerAngleDeg * Math.PI) / 180;
  let spawned = 0;
  for (let i = 0; i < opts.count; i++) {
    // Distribute evenly across arc; for count=1, place at center.
    const t = opts.count > 1 ? i / (opts.count - 1) : 0.5;
    const angle = center - span / 2 + t * span;
    const x = px + Math.cos(angle) * opts.distanceFromPlayerWu;
    const y = py + Math.sin(angle) * opts.distanceFromPlayerWu;
    debugSpawnEnemy(def, x, y);
    spawned++;
  }
  return spawned;
}

// BETA 14.32 Phase 3 — floor-effect tracker. Module-level cache populated
// by engine.ts via setFloorEffectTracker (per-frame poll of weaponSystem
// state.groundEffects). Cleared on applyBuild via clearTrackers.
type FloorEffectTrackerEntry = {
  spawns: number; activeFramesAvg: number; lifetimeAvgMs: number;
  enemyTriggers: number; dmgDealt: number; statusFramesAttributed: number;
  playerStoodInFrames: number; kills: number; maxStack: number;
};
let _localFloorEffectTracker: Record<string, FloorEffectTrackerEntry> = {};
export function setFloorEffectTracker(t: Record<string, FloorEffectTrackerEntry>): void {
  _localFloorEffectTracker = t;
}
export function getLocalFloorEffectTracker(): Record<string, FloorEffectTrackerEntry> {
  return _localFloorEffectTracker;
}

// BETA 14.55 — Floor-effect tracker increment helpers. Engine.ts wires these
// to weaponSystem._floorEffectTracker via registerFloorEffectTracker. Each
// hook accumulates per-weaponId stats into _localFloorEffectTracker. Reset
// happens in clearTrackers per build.
function _ensureFloorEntry(weaponId: string): FloorEffectTrackerEntry {
  let e = _localFloorEffectTracker[weaponId];
  if (!e) {
    e = {
      spawns: 0, activeFramesAvg: 0, lifetimeAvgMs: 0,
      enemyTriggers: 0, dmgDealt: 0, statusFramesAttributed: 0,
      playerStoodInFrames: 0, kills: 0, maxStack: 0,
    };
    _localFloorEffectTracker[weaponId] = e;
  }
  return e;
}
export function trackFloorEffectSpawn(weaponId: string, lifetime: number): void {
  const e = _ensureFloorEntry(weaponId);
  e.spawns += 1;
  // Running avg of lifetimeAvgMs (in milliseconds)
  e.lifetimeAvgMs = (e.lifetimeAvgMs * (e.spawns - 1) + lifetime * 1000) / e.spawns;
}
export function trackFloorEffectTick(weaponId: string, dmg: number): void {
  const e = _ensureFloorEntry(weaponId);
  e.dmgDealt += dmg;
}
export function trackFloorEffectFirstContact(weaponId: string): void {
  const e = _ensureFloorEntry(weaponId);
  e.enemyTriggers += 1;
}

// BETA 14.32 Phase 3 — per-frame sweep counters. Engine pushes each frame.
interface SweepCounters {
  playerHpBucketLowFrames: number;
  playerHpBucketMidFrames: number;
  playerHpBucketHighFrames: number;
  playerStationaryFrames: number;
  playerDiagonalMoveFrames: number;
  noEnemyNearbyFrames: number;
  fivePlusEnemiesIn150wuFrames: number;
  playerMSAvg: number;
  phaseChanceCurrentAvg: number;
  critChanceCurrentAvg: number;
  // Ugly Kitten special tracking (9 cols)
  kittenPlacementsCount: number;
  kittenActiveAtAnyTimeAvg: number;
  kittenAttacksFiredTotal: number;
  kittenDmgDealtTotal: number;
  kittenKillsTotal: number;
  kittenFreezeAppliedFrames: number;
  kittenLifetimeAvgMs: number;
  kittenDeathRate: number;
  kittenPlacementCornerCount: number;
  kittenPlacementCenterCount: number;
  kittenPlacementScatterCount: number;
}
let _cachedSweepCounters: SweepCounters = {
  playerHpBucketLowFrames: 0,
  playerHpBucketMidFrames: 0,
  playerHpBucketHighFrames: 0,
  playerStationaryFrames: 0,
  playerDiagonalMoveFrames: 0,
  noEnemyNearbyFrames: 0,
  fivePlusEnemiesIn150wuFrames: 0,
  playerMSAvg: 0,
  phaseChanceCurrentAvg: 0,
  critChanceCurrentAvg: 0,
  kittenPlacementsCount: 0,
  kittenActiveAtAnyTimeAvg: 0,
  kittenAttacksFiredTotal: 0,
  kittenDmgDealtTotal: 0,
  kittenKillsTotal: 0,
  kittenFreezeAppliedFrames: 0,
  kittenLifetimeAvgMs: 0,
  kittenDeathRate: 0,
  kittenPlacementCornerCount: 0,
  kittenPlacementCenterCount: 0,
  kittenPlacementScatterCount: 0,
};
export function setCurrentSweepCounters(s: Partial<SweepCounters>): void {
  _cachedSweepCounters = { ..._cachedSweepCounters, ...s };
}

// BETA 14.54/14.56 — Ugly Kittens telemetry counters. Module-level so they
// survive across builds independent of GameState fields (which the existing
// _cachedSweepCounters wired to but were never incremented). weaponSystem.ts
// calls these via the _kittenEventTracker callback registered in engine.ts.
// BETA 14.56 expands to include kills, lifetime running average, and
// corner/center/scatter coord buckets at placement.
let _localKittenPlacements = 0;
let _localKittenAttacks = 0;
let _localKittenDmg = 0;
let _localKittenKills = 0;
let _localKittenLifetimesMs = 0;     // sum of all observed lifetimes
let _localKittenLifetimeCount = 0;   // count of dead kittens (for averaging)
let _localKittenCornerCount = 0;
let _localKittenCenterCount = 0;
let _localKittenScatterCount = 0;
const _WORLD_HALF = 9000; // Half of WORLD_SIZE (18000). Used for coord bucket
const _KITTEN_CENTER_RADIUS_SQ = 2000 * 2000;  // dist from center < 2000 = "center"
const _KITTEN_CORNER_AXIS_DIST = 6000;          // both axes > 6000 from center = "corner"
function _bucketKittenCoord(x: number, y: number): 'corner' | 'center' | 'scatter' {
  const dx = x - _WORLD_HALF;
  const dy = y - _WORLD_HALF;
  if (dx * dx + dy * dy < _KITTEN_CENTER_RADIUS_SQ) return 'center';
  if (Math.abs(dx) > _KITTEN_CORNER_AXIS_DIST && Math.abs(dy) > _KITTEN_CORNER_AXIS_DIST) return 'corner';
  return 'scatter';
}
export function trackKittenPlace(x: number, y: number): void {
  _localKittenPlacements++;
  const bucket = _bucketKittenCoord(x, y);
  if (bucket === 'center') _localKittenCenterCount++;
  else if (bucket === 'corner') _localKittenCornerCount++;
  else _localKittenScatterCount++;
}
export function trackKittenAttack(): void { _localKittenAttacks++; }
export function trackKittenHit(dmg: number, wasKill: boolean): void {
  _localKittenDmg += dmg;
  if (wasKill) _localKittenKills++;
}
export function trackKittenProjectileDeath(lifetimeMs: number): void {
  _localKittenLifetimesMs += lifetimeMs;
  _localKittenLifetimeCount++;
}
export function getLocalKittenStats(): {
  placements: number; attacks: number; dmg: number; kills: number;
  lifetimeAvgMs: number; cornerCount: number; centerCount: number; scatterCount: number;
} {
  return {
    placements: _localKittenPlacements,
    attacks: _localKittenAttacks,
    dmg: _localKittenDmg,
    kills: _localKittenKills,
    lifetimeAvgMs: _localKittenLifetimeCount > 0 ? _localKittenLifetimesMs / _localKittenLifetimeCount : 0,
    cornerCount: _localKittenCornerCount,
    centerCount: _localKittenCenterCount,
    scatterCount: _localKittenScatterCount,
  };
}

// BETA 14.33 Phase 4 — weapon × enemy matrix + synergy tracker.
type WeaponEnemyEntry = { shots: number; hits: number; crits: number; dmg: number };
let _localWeaponEnemyMatrix: Record<string, Record<string, WeaponEnemyEntry>> = {};
export function trackWeaponHit(weaponId: string, enemyType: string, dmg: number, isCrit: boolean): void {
  const wEntry = _localWeaponEnemyMatrix[weaponId] ?? {};
  const eEntry = wEntry[enemyType] ?? { shots: 0, hits: 0, crits: 0, dmg: 0 };
  eEntry.hits += 1;
  eEntry.dmg += dmg;
  if (isCrit) eEntry.crits += 1;
  wEntry[enemyType] = eEntry;
  _localWeaponEnemyMatrix[weaponId] = wEntry;
}
export function trackWeaponShotFired(weaponId: string, enemyType: string): void {
  const wEntry = _localWeaponEnemyMatrix[weaponId] ?? {};
  const eEntry = wEntry[enemyType] ?? { shots: 0, hits: 0, crits: 0, dmg: 0 };
  eEntry.shots += 1;
  wEntry[enemyType] = eEntry;
  _localWeaponEnemyMatrix[weaponId] = wEntry;
}
export function getLocalWeaponEnemyMatrix(): Record<string, Record<string, WeaponEnemyEntry>> {
  return _localWeaponEnemyMatrix;
}

type SynergyEntry = { bothEquipped: boolean; triggerCount?: number; valueDelta?: number };
let _localSynergyTracker: Record<string, SynergyEntry> = {};
export function setSynergyTracker(t: Record<string, SynergyEntry>): void {
  _localSynergyTracker = t;
}
export function getLocalSynergyTracker(): Record<string, SynergyEntry> {
  return _localSynergyTracker;
}

// Computed at row-emit time from the matrix.
function _computeTopWeaponVsEnemy(matrix: Record<string, Record<string, WeaponEnemyEntry>>, enemyType: string): string {
  let topId = '';
  let topDmg = 0;
  for (const wId in matrix) {
    const e = matrix[wId][enemyType];
    if (e && e.dmg > topDmg) { topDmg = e.dmg; topId = wId; }
  }
  return topId;
}
function _computeWeakestWeapon(matrix: Record<string, Record<string, WeaponEnemyEntry>>): string {
  let weakestId = '';
  let weakestRatio = Infinity;
  for (const wId in matrix) {
    let totalDmg = 0;
    let totalShots = 0;
    for (const eType in matrix[wId]) {
      totalDmg += matrix[wId][eType].dmg;
      totalShots += matrix[wId][eType].shots;
    }
    if (totalShots > 0) {
      const ratio = totalDmg / totalShots;
      if (ratio < weakestRatio) { weakestRatio = ratio; weakestId = wId; }
    }
  }
  return weakestId;
}
export function computeTopWeaponSummary(): {
  // 23.39 — topVsCaseoh stays as a PSBOT-compat field (always '' now; CaseOh purged, handoff sent)
  topVsZombie: string; topVsTimmy: string; topVsCaseoh: string; topVsElite: string; weakest: string;
} {
  return {
    topVsZombie: _computeTopWeaponVsEnemy(_localWeaponEnemyMatrix, 'zombie'),
    // E1 (enemy rebuild) — timmy/caseoh can never appear in the hit matrix
    // (both purged from ENEMY_DATABASE); hardcode the permanent '' the
    // computes already returned. Column names topWeaponVsTimmy/topWeaponVsCaseoh
    // stay in every CSV forever per PSBOT retired-column law.
    topVsTimmy: '',
    topVsCaseoh: '',
    topVsElite: _computeTopWeaponVsEnemy(_localWeaponEnemyMatrix, 'named_elite'),
    weakest: _computeWeakestWeapon(_localWeaponEnemyMatrix),
  };
}

// BETA 14.33 Phase 4 — synergy bothEquipped detection. Scans inventory and
// marks each known synergy pair's bothEquipped flag based on whether both
// items/weapons are present. Trigger counts + valueDelta deferred (per-event
// instrumentation needed for those at item proc sites).
const SYNERGY_PAIRS: Array<[string, string[]]> = [
  ['sheeesh_liarDeceiver', ['item_sheeesh', 'item_liar_deceiver_false_prophet']],
  ['dracs_fang_gas_mask', ['wpn_dracs_fang', 'item_dracs_gas_mask']],
  ['frozenDarts_stayFrosty', ['wpn_frozen_darts', 'item_stay_frosty']],
  ['squirter_frozenDarts', ['wpn_squirter', 'wpn_frozen_darts']],
  ['whoopieCushion_asmonsBanana', ['item_whoopie_cushion_of_doom', 'item_asmons_desk_banana']],
  ['iranianBird_bungulators', ['item_iranian_bird', 'item_bungulators']],
  ['caseohCooking_voogooRager', ['item_caseoh_moms_cooking', 'item_voogoo_rager']],
  ['bigMacSauce_stayFrosty', ['item_big_mac_special_sauce', 'item_stay_frosty']],
  ['timmyRepellent_stompatine', ['item_timmy_repellent_spray', 'N11']],
  ['writeThatDown_chestBuilds', ['item_write_that_down', 'C05']],
  ['notBlueChew_hutchsArmor', ['N04', 'N02']],   // BETA 19.98 — pair was wrong (T06 = Vision, not Hutch's Armor). Trigger fires on N04+N02 — pair now matches.
  ['greedyTums_stepmomsPills', ['N09', 'T10']],
  ['karenDrones_bungulators', ['wpn_karen_drones', 'item_bungulators']],
  ['chadAura_dracsFang', ['chad_aura', 'wpn_dracs_fang']],
  ['notAcid_hutchsArmor', ['N14', 'N02']],   // BETA 19.98 — pair was wrong (T06 = Vision, not Hutch's Armor). Trigger fires on N14+N02 — pair now matches.
  ['pubertyBlockers_writeThatDown', ['N07', 'item_write_that_down']],
  ['voogooRager_dracsGasMask', ['item_voogoo_rager', 'item_dracs_gas_mask']],
  ['mysteryMeatStew_kills', ['item_mystery_meat_stew', '*']],   // any kill source
  ['notXanax_movementSpeed', ['N13', '*']],   // any MS source
  // BETA 19.98 — letsRide_kiteBuilds removed. N06 perc was deleted in 17.88
  // (was a no-op since 15.13). Synergy could never fire.
];
export function refreshSynergyTracker(equippedIds: Set<string>): void {
  const next: Record<string, SynergyEntry> = {};
  for (const [key, pair] of SYNERGY_PAIRS) {
    const a = pair[0];
    const b = pair[1];
    const bothEquipped = equippedIds.has(a) && (b === '*' || equippedIds.has(b));
    const existing = _localSynergyTracker[key] ?? { bothEquipped: false };
    next[key] = { ...existing, bothEquipped };
  }
  _localSynergyTracker = next;
}
/** BETA 14.58 — increment a synergy pair's triggerCount when the synergy
 *  actually fires (e.g. dracs_fang_gas_mask boosting lifesteal application,
 *  sheeesh_liarDeceiver firing the second revive, frozen+stayFrosty chaining
 *  freeze). Auto-creates entry if missing.
 *
 *  BETA 14.71 — BUG-8 from cycle-8: triggerCount was firing even when only
 *  ONE half of the synergy was equipped (e.g. notXanax_movementSpeed had
 *  triggerCount:1004 but bothEquipped:false because N13 alone was equipped
 *  but no MS source). Now gated on bothEquipped == true. The increment skips
 *  silently when the pair isn't both equipped — analyst gets accurate
 *  "synergy fired this many times when both were actually present" data. */
export function trackSynergyTrigger(key: string, valueDelta: number = 0): void {
  const e = _localSynergyTracker[key];
  if (!e || !e.bothEquipped) return; // BUG-8 gate: require both equipped
  e.triggerCount = (e.triggerCount ?? 0) + 1;
  if (valueDelta !== 0) e.valueDelta = (e.valueDelta ?? 0) + valueDelta;
}

// BETA 14.09 — Wave / time lock. When set, engine.ts clamps state.time to
// this value each frame so the wave-system difficulty scaling, swarm spawns,
// and enemy stat ramps stay frozen at the chosen minute. User: "i would like
// to lock a level i am on. not really timer based. just allows me to control
// what wave i am on". Persisted via localStorage so the lock survives lab
// reloads — minute number directly (null = unlocked).
const LAB_FROZEN_TIME_KEY = 'goopzone_lab_frozen_time_v1';
let _labFrozenTimeSec: number | null = (() => {
  try {
    const raw = localStorage.getItem(LAB_FROZEN_TIME_KEY);
    if (!raw) return null;
    const n = parseFloat(raw);
    return Number.isFinite(n) && n >= 0 ? n : null;
  } catch { return null; }
})();
export function getLabFrozenTimeSec(): number | null { return _labFrozenTimeSec; }
export function setLabFrozenTimeSec(secs: number | null): void {
  _labFrozenTimeSec = secs;
  try {
    if (secs == null) localStorage.removeItem(LAB_FROZEN_TIME_KEY);
    else localStorage.setItem(LAB_FROZEN_TIME_KEY, String(secs));
  } catch { /* ignore */ }
}

// ─── BETA 14.09 — Perk Toggle (lab perc-stacking) ────────────────────────────
// User: "i should be able to stack perks as well right? not just buncha
// bullshiii". The level-up screen normally adds one level per pick; in lab
// we want a "+1" click that immediately bumps a perc's level so testers can
// stack rapidly without waiting for level-ups. Wraps the existing slot-level
// + accumulatedBonus pattern from selectLevelUpOption (peanutPercSystem.ts).
import { getPercState, SHRINE_BUFF_POOL, applyShrineBuffById, clearAppliedShrineBuffs } from './itemsAdapter';
import { PEANUT_PERCS } from './peanutPercs';

/** Returns [{ id, name, level, maxLevel, slotIndex }] for every perc, with
 *  level 0 / slotIndex -1 if not currently slotted. */
export function listAllPercsWithState(): Array<{
  id: string; name: string; level: number; maxLevel: number; slotIndex: number;
}> {
  const ps = getPercState();
  return PEANUT_PERCS.map(p => {
    const idx = ps?.slots.findIndex(s => s.perc?.id === p.id) ?? -1;
    const slot = idx >= 0 ? ps?.slots[idx] : undefined;
    return {
      id: p.id,
      name: p.name,
      level: slot?.level ?? 0,
      maxLevel: p.maxLevel,
      slotIndex: idx,
    };
  });
}

/** Increment a perc's level by 1. If the perc isn't slotted, drops it into
 *  the first empty slot at level 1. Caps at perc.maxLevel. Returns the new
 *  level (or 0 if no slot was available + perc is unslotted). */
export function incrementPercLevel(percId: string): number {
  const ps = getPercState();
  if (!ps) return 0;
  const perc = PEANUT_PERCS.find(p => p.id === percId);
  if (!perc) return 0;
  // BETA 14.31 Phase 2 — perc tracker apply event. Track will run before
  // the slot mutation below; we read the resulting newStack at the end.
  // Find existing slot for this perc
  let slot = ps.slots.find(s => s.perc?.id === percId);
  if (!slot) {
    // Find empty slot
    slot = ps.slots.find(s => !s.perc);
    if (!slot) {
      // No empty slot — overwrite the lowest-level non-matching slot (lab is
      // for testing; we accept the destructive behavior here). If all slots
      // are full and at high level, this still picks the lowest as a fallback.
      slot = ps.slots.reduce((min, s) => (s.level < min.level ? s : min), ps.slots[0]);
      if (!slot) return 0;
    }
    slot.perc = perc;
    slot.level = 1;
    slot.accumulatedBonus = perc.bonusPerLevel;
    trackPercApply(percId, 1);
    return 1;
  }
  if (slot.level >= perc.maxLevel) return slot.level; // capped
  slot.level += 1;
  slot.accumulatedBonus += perc.bonusPerLevel;
  trackPercApply(percId, slot.level);
  return slot.level;
}

/** Reset a perc to level 0 (clears the slot). */
export function clearPercFromSlot(percId: string): void {
  const ps = getPercState();
  if (!ps) return;
  const slot = ps.slots.find(s => s.perc?.id === percId);
  if (!slot) return;
  slot.perc = null;
  slot.level = 0;
  slot.accumulatedBonus = 0;
}

// ─── Weapon Toggle ────────────────────────────────────────────────────────────
export function setWeaponEquipped(weaponId: string, equipped: boolean, rarity: WeaponRarity = 'common'): void {
  const ws = getWeaponSystemState();
  if (!ws) return;
  const has = hasWeapon(ws, weaponId);
  if (equipped && !has) equipWeapon(ws, weaponId, rarity);
  else if (!equipped && has) unequipWeapon(ws, weaponId);
}

export function getEquippedWeaponIds(): string[] {
  const ws = getWeaponSystemState();
  if (!ws) return [];
  return ws.weapons.map((w: WeaponInstance) => w.definitionId);
}

export function listAllWeapons(): Array<{ id: string; name: string }> {
  return WEAPONS.map(w => ({ id: w.id, name: w.name }));
}

// ─── Item Toggle ──────────────────────────────────────────────────────────────
export function setItemEquipped(itemId: string, equipped: boolean): void {
  const is = getItemState();
  if (!is) return;
  const has = is.inventory.some(s => s.item.id === itemId);
  if (equipped && !has) addBullshiiii(is, itemId);
  else if (!equipped && has) removeBullshiiii(is, itemId);
}

export function setItemStacks(itemId: string, stacks: number): void {
  const is = getItemState();
  if (!is) return;
  // Remove fully then re-add the requested number of times.
  while (is.inventory.some(s => s.item.id === itemId)) removeBullshiiii(is, itemId);
  for (let i = 0; i < Math.max(0, Math.floor(stacks)); i++) addBullshiiii(is, itemId);
  // BETA 14.31 Phase 2 — track item stack change. Note: this fires on
  // every setItemStacks call (including the clear-to-0 step in applyBuild),
  // and trackItemStackChange itself only counts new applies when stack grows.
  trackItemStackChange(itemId, Math.max(0, Math.floor(stacks)));
}

export function getEquippedItems(): Array<{ id: string; name: string; stacks: number }> {
  const is = getItemState();
  if (!is) return [];
  return is.inventory.map(s => ({ id: s.item.id, name: s.item.name, stacks: s.stacks }));
}

export function listAllItems(): Array<{ id: string; name: string; rarity: string }> {
  // BETA 19.6 — Filter out shelved items so they don't appear in the lab
  // picker. Pre-19.6, all 52 items (including 8 shelved tier-duplicates +
  // Hutch's Lucky Horseshoe shelved in 19.5) were clickable in the lab
  // picker even though the chest/shrine/levelup rolls exclude them. Lab
  // builds with shelved items diverged from real-game rolls, polluting
  // CSV data and bot training.
  return BUNCHA_BULLSHIIII
    .filter(i => i.status === 'active')
    .map(i => ({ id: i.id, name: i.name, rarity: i.rarity }));
}

// ─── Ultra Item Toggle (BETA 20.48) ───────────────────────────────────────────
// The 7 ultra "boss chest" items (ultraItems.ts) are a SEPARATE subsystem from
// regular items — granted via ultraChestPool.collect(id, state), which needs the
// LIVE full GameState (player + enemies + ultraState), not just getItemState().
// window.__getGameState() (defined in GameCanvas) returns the live state.
// Toggle-off calls ultraChestPool.remove(), which stops the per-frame onUpdate
// and runs each item's onRemove cleanup (e.g. disband followers, undo Grind dmg).
function _liveGameState(): GameState | null {
  return (window as unknown as { __getGameState?: () => GameState | null }).__getGameState?.() ?? null;
}

export function listAllUltraItems(): Array<{ id: string; name: string; description: string }> {
  return ULTRA_ITEMS.map(u => ({ id: u.id, name: u.name, description: u.description }));
}

export function getEquippedUltraItemIds(): string[] {
  return ULTRA_ITEMS.filter(u => ultraChestPool.isCollected(u.id)).map(u => u.id);
}

export function setUltraItemEquipped(itemId: string, equipped: boolean): void {
  const st = _liveGameState();
  if (!st) return;
  if (equipped) ultraChestPool.collect(itemId, st);
  else ultraChestPool.remove(itemId, st);
}

// ─── Enemy Spawn ──────────────────────────────────────────────────────────────
export function listAllEnemyTypes(): Array<{ id: string; name: string }> {
  return Object.values(ENEMY_DATABASE).map(e => ({ id: e.id, name: e.name }));
}

/** Spawn `count` enemies of the given type in a ring around (cx, cy). */
export function spawnEnemiesAround(definitionId: string, count: number, cx: number, cy: number, ringRadius = 280): void {
  const n = Math.max(1, Math.floor(count));
  for (let i = 0; i < n; i++) {
    const angle = (i / n) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
    const r = ringRadius + Math.random() * 80;
    const x = cx + Math.cos(angle) * r;
    const y = cy + Math.sin(angle) * r;
    debugSpawnEnemy(definitionId, x, y);
  }
}

export function getAliveEnemyCounts(): Record<string, number> {
  const es = getEnemySystemState();
  if (!es) return {};
  const counts: Record<string, number> = {};
  for (const e of es.enemies) {
    if (!e.alive) continue;
    counts[e.definitionId] = (counts[e.definitionId] ?? 0) + 1;
  }
  return counts;
}

export function clearAllEnemies(): void {
  const es = getEnemySystemState();
  if (!es) return;
  for (const e of es.enemies) e.alive = false;
  // Force the alive counter to resync — cleanupDead in updateEnemies will tidy on next frame.
  es.aliveCount = 0;
}

// ─── Note + CSV Accumulator ───────────────────────────────────────────────────
const STORAGE_KEY = 'goopzone_weapon_lab_v1';

export interface LabNote {
  /** ISO timestamp at the moment "Save Note" was clicked. */
  timestamp: string;
  /** Random per-session id so the user can group notes from the same lab session. */
  sessionId: string;
  /** Character name at note time (e.g. "Cowboy Peanut"). */
  character: string;
  /** Comma-separated list of weapon IDs equipped at note time. */
  weapons: string;
  /** Comma-separated list of item entries: "id:stacks". */
  items: string;
  /** Comma-separated list of enemy entries alive at note time: "id:count". */
  enemies: string;
  /** Free-text notes typed by the user. */
  notes: string;
  // BETA 14.14 — Telemetry snapshot fields (captured at saveLabNote time)
  /** Pipe-separated per-weapon telemetry: "id:lvl:dmg:hits/fired:crits".
   *  Mirrors the bot CSV weaponTelemetry column. Empty if no weapons equipped. */
  weaponTelemetry?: string;
  /** Pipe-separated per-perc current levels: "id:level" for slotted percs. */
  percLevels?: string;
  /** Pipe-separated current ground-effect counts by weaponId. */
  groundEffectCounts?: string;
}

export function loadLabNotes(): LabNote[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as LabNote[];
  } catch {
    return [];
  }
}

function _saveLabNotesArr(arr: LabNote[]): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(arr)); }
  catch (e) { console.warn('[WeaponLab] Failed to persist notes:', e); }
}

/** Snapshot the current loadout + alive enemies + per-weapon telemetry, persist.
 *  BETA 14.14 — Telemetry snapshot fields (weaponTelemetry, percLevels,
 *  groundEffectCounts) capture mid-run wild-data so the user can hand-stack
 *  a setup, fight some enemies, click SAVE NOTE, and get the same per-weapon
 *  damage / accuracy / crit signals the bot CSV captures. NOT identical to
 *  the bot's 57-column format yet (waiting on Manus's getCurrentTelemetry-
 *  SnapshotAsCsvRow helper) — but covers the most-asked-for fields. */
export function saveLabNote(args: { sessionId: string; character: string; notes: string }): LabNote {
  const items = getEquippedItems();
  const enemies = getAliveEnemyCounts();

  // ── BETA 14.14 — Snapshot per-weapon runtime telemetry ──
  const ws = getWeaponSystemState();
  const weaponTelemetry = ws
    ? ws.weapons.map((w: WeaponInstance) => {
        const dmg = Math.round(w.totalDamageDealt ?? 0);
        const hits = w.totalShotsHit ?? 0;
        const fired = w.totalShotsFired ?? 0;
        const crits = w.totalCrits ?? 0;
        return `${w.definitionId}:lvl${w.level}:dmg${dmg}:hits${hits}/${fired}:crits${crits}`;
      }).join('|')
    : '';

  // ── BETA 14.14 — Snapshot perc levels ──
  const ps = getPercState();
  const percLevels = ps
    ? ps.slots
        .filter(s => s.perc && s.level > 0)
        .map(s => `${s.perc!.id}:lvl${s.level}`)
        .join('|')
    : '';

  // ── BETA 14.14 — Snapshot ground-effect counts by weaponId ──
  const groundEffectCounts = ws
    ? (() => {
        const counts: Record<string, number> = {};
        for (const ge of ws.groundEffects) {
          counts[ge.weaponId] = (counts[ge.weaponId] ?? 0) + 1;
        }
        return Object.entries(counts).map(([id, c]) => `${id}:${c}`).join('|');
      })()
    : '';

  const note: LabNote = {
    timestamp: new Date().toISOString(),
    sessionId: args.sessionId,
    character: args.character,
    weapons: getEquippedWeaponIds().join(','),
    items: items.map(i => `${i.id}:${i.stacks}`).join(','),
    enemies: Object.entries(enemies).map(([id, c]) => `${id}:${c}`).join(','),
    notes: args.notes,
    weaponTelemetry,
    percLevels,
    groundEffectCounts,
  };
  const all = loadLabNotes();
  all.push(note);
  _saveLabNotesArr(all);
  return note;
}

/** Generate a CSV blob for download. Columns match the LabNote interface.
 *  BETA 14.14 — added weaponTelemetry, percLevels, groundEffectCounts columns. */
export function buildLabNotesCsv(notes: LabNote[] = loadLabNotes()): string {
  const header = [
    'timestamp', 'sessionId', 'character',
    'weapons', 'items', 'enemies',
    'weaponTelemetry', 'percLevels', 'groundEffectCounts',
    'notes',
  ];
  const escape = (s: string) => `"${String(s).replace(/"/g, '""')}"`;
  const rows = notes.map(n => header.map(h => escape((n as unknown as Record<string, string>)[h] ?? '')).join(','));
  return [header.join(','), ...rows].join('\n');
}

export function downloadLabCsv(): void {
  const csv = buildLabNotesCsv();
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `goopzone_weapon_lab_${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function clearLabNotes(): void {
  try { localStorage.removeItem(STORAGE_KEY); }
  catch (e) { console.warn('[WeaponLab] Failed to clear notes:', e); }
}

/** Generate a fresh sessionId — call once per WeaponLabScreen mount. */
export function newLabSessionId(): string {
  return `lab_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// BETA 14.15 — RANDOMIZER + PRESETS + MULTI-BUILD SESSION CSV
// (Manus handoff "weaponlab-randomizer-handoff")
// ═══════════════════════════════════════════════════════════════════════════
//
// Workflow: user clicks 🎲 RANDOMIZE or picks a preset → finalize previous
// row + apply new loadout + reset combat metrics + start new row. Each row =
// per-build snapshot. ↓CSV at any time exports the accumulated session.

interface BuildRow {
  buildName: string;
  buildSource: 'randomizer' | 'preset' | 'custom';
  buildStartedAt: number;
  /** BETA 14.16 — shrines + percs applied at build time, captured for the
   *  CSV's shrineBuffs + percLevels columns. */
  shrines?: string[];
  percs?: string[];
  percLevel?: number;
  /** BETA 14.27 — snapshot of combat stat accumulators at build start.
   *  _buildRowSnapshot subtracts these from the current values to produce
   *  per-build deltas (damageTaken / attacksAttempted / etc are run-cumulative
   *  in state, not per-build). */
  startCombatStats?: CombatStats;
  /** Filled at finalize time — full CSV row. */
  finalizedRow?: string;
}

const _sessionBuildRows: BuildRow[] = [];
let _activeBuildRow: BuildRow | null = null;

const _seenWeaponsThisSession = new Set<string>();
const _seenItemsThisSession = new Set<string>();
const _seenShrinesThisSession = new Set<string>();
const _seenPercsThisSession = new Set<string>();
let _randomBuildCounter = 0;

// BETA 17.84 — MAX-build pick counts re-calibrated for a realistic 10-min
// run. Dylan: "a max build should be a poissibility in a 10 minute run.
// like how many chest, and shrines, and level up is possible to get in
// 10 minutes? that would let you know what items and such and how many
// they should have for max build."
//
// Grounded in: 98 stored bot runs (best ever = 4.8 min: lvl 24, 8 chests,
// 0 shrines, 34 item-stack entries). Extrapolated to 10 min (if bot
// survives that long): ~12-15 chests, ~30-35 level-ups, 5-10 shrines
// (player would activate; bot historically didn't). Map content limits
// the ceiling regardless: 106 total chests, 50 buff shrines, 53 rooms.
//
// Previous (BETA 14.16) values: 4w / 10i / 15s / 4p × lvl6. Shrine count
// was fantasy (15 in 5 min). Perc shape was wrong (4 percs at lvl 6 =
// 24 levels; players actually spread XP across more percs at lower lvl).
const PICKS_PER_BUILD = {
  weapons: 4,    // 4 = game cap, unchanged
  items: 12,     // 12 chest items in 10 min (best recorded run had 8 in 4.8 min)
  shrines: 8,    // 8 shrine activations in 10 min (realistic player pace)
  percs: 12,     // 12 different percs touched (up from 4 — distributed level-up rewards)
  percLevel: 3,  // avg level per perc slot (down from 6 — total 36 perc-levels matches 30-35 level-ups)
} as const;

// BETA 17.86 — LUDICROUS build (REWRITE per Dylan: "ludacris mode needs
// 5 of each item added, needs perks to be level 15 each that it has.
// but what about the shrines? we need to have a ranamizes 20 shrine
// upgrades on each random ludacris build.")
//
// Game has a 4-perc-slot hard cap (MAX_PERC_SLOTS = 4). Each slot can
// LEVEL UP though — so LUDICROUS picks 4 random percs and levels each
// to 15. Items get 5 stacks each (capped at maxStacks per item def).
// Shrines: 20 random buffs (not all 80+) so each LUDICROUS roll has
// build variety. Goal: stress-test for OP/weak/broken at a build
// strength well above realistic-MAX, but still respecting the slot cap.
const LUDICROUS_PERC_LEVEL = 15;       // level of each of the 4 picked percs
const LUDICROUS_PERCS_PICKED = 4;       // matches MAX_PERC_SLOTS — slot cap
const LUDICROUS_ITEM_STACKS = 5;        // 5 stacks per item, capped at maxStacks
const LUDICROUS_SHRINES_PICKED = 20;    // 20 random shrine buffs per build

// BETA 17.92 — STRESS preset. Per Dylan: "set the time to 47 hours. give our
// player 87 items each. level up everything the player has including shrines
// to match how many that would actually be." Matches what a top-of-leaderboard
// Megabonk-equivalent ~47-hr run would look like. NOT meant for balance data
// — purpose is render-load + entity-count stress testing.
// BETA 17.93 — Dylan: "now do 100 of everything!!!!" + the prior STRESS was
// silently capped at each item's maxStacks (3-5 typical) so only 1/49 items
// actually hit 87. Now uses `forceStacks` to BYPASS maxStacks entirely.
const STRESS_PERC_LEVEL = 100;          // 100-level percs (way beyond LUDICROUS's 15)
const STRESS_PERCS_PICKED = 4;          // slot cap unchanged
const STRESS_ITEM_STACKS = 100;         // 100 stacks per item — IGNORES per-item maxStacks ceiling
// Shrines: apply EVERY active shrine — not a random 20.

function _shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function _drawWithRoundRobin<T>(pool: T[], seen: Set<T>, n: number): T[] {
  const unseen = pool.filter(p => !seen.has(p));
  if (unseen.length < n) seen.clear();
  const drawPool = unseen.length >= n ? unseen : pool;
  const drawn = _shuffle(drawPool).slice(0, n);
  for (const d of drawn) seen.add(d);
  return drawn;
}

/** BETA 14.16 — MAX-build round-robin pick: 4 weapons + 10 items + 15 shrine
 *  buffs + 4 percs at level 6. After 7 randomizes the user has seen every
 *  weapon + every item + every shrine + every perc at least once. */
export interface RandomBuild {
  buildName: string;
  weapons: string[];
  items: string[];
  shrines: string[];
  percs: string[];
  percLevel: number;
  // BETA 17.86 — LUDICROUS override: when set, applyBuild() uses min(this, maxStacks)
  // instead of the default maxStacks for every item. Lets LUDICROUS specify
  // "5 stacks per item" while MAX stays at the per-item-default behavior.
  itemStacksOverride?: number;
  // BETA 17.93 — STRESS: when set, applyBuild() BYPASSES maxStacks entirely
  // and forces raw stack count for every stackable item. Non-stackable items
  // still get 1. Dylan wanted literal 100 of each — was getting 3-5 capped.
  forceStacks?: number;
}
export function randomizeBuild(): RandomBuild {
  const allWeapons = WEAPONS.map(w => w.id);
  const allItems = BUNCHA_BULLSHIIII.filter(i => i.status === 'active').map(i => i.id);
  const allShrines = SHRINE_BUFF_POOL.filter(b => b.rarity !== 'negative').map(b => b.id);
  const allPercs = PEANUT_PERCS.map(p => p.id);

  const weapons = _drawWithRoundRobin(allWeapons, _seenWeaponsThisSession, PICKS_PER_BUILD.weapons);
  const items   = _drawWithRoundRobin(allItems,   _seenItemsThisSession,   PICKS_PER_BUILD.items);
  const shrines = _drawWithRoundRobin(allShrines, _seenShrinesThisSession, PICKS_PER_BUILD.shrines);
  const percs   = _drawWithRoundRobin(allPercs,   _seenPercsThisSession,   PICKS_PER_BUILD.percs);

  _randomBuildCounter++;
  return {
    buildName: `MAX #${_randomBuildCounter}`,
    weapons,
    items,
    shrines,
    percs,
    percLevel: PICKS_PER_BUILD.percLevel,
  };
}

// BETA 17.86 — LUDICROUS build (REWRITE). Per Dylan correction:
// - 4 random percs at level 15 each (4 is the slot cap; each slot levels up)
// - 5 stacks of every item (capped at each item's maxStacks)
// - 20 random shrine upgrades per build
// - 4 random weapons (game cap)
//
// Not achievable in a real run — purpose is to stress-test every system
// at high but bounded strength so we can spot OP / weak / broken items.
let _ludicrousCounter = 0;
export function randomizeLudicrousBuild(): RandomBuild {
  const allWeapons = WEAPONS.map(w => w.id);
  const allItems = BUNCHA_BULLSHIIII.filter(i => i.status === 'active').map(i => i.id);
  const allShrines = SHRINE_BUFF_POOL.filter(b => b.rarity !== 'negative').map(b => b.id);
  const allPercs = PEANUT_PERCS.map(p => p.id);

  // 4 random weapons (game cap)
  const weapons = _shuffle(allWeapons).slice(0, PICKS_PER_BUILD.weapons);
  // 4 random percs — each will be levelled to 15 via percLevel below
  const percs = _shuffle(allPercs).slice(0, LUDICROUS_PERCS_PICKED);
  // 20 random shrine buffs
  const shrines = _shuffle(allShrines).slice(0, LUDICROUS_SHRINES_PICKED);

  _ludicrousCounter++;
  return {
    buildName: `LUDICROUS #${_ludicrousCounter}`,
    weapons,
    items:   allItems,    // every active item — applyBuild stacks each at min(5, maxStacks)
    shrines,              // 20 random buffs
    percs,                // 4 random percs
    percLevel: LUDICROUS_PERC_LEVEL,         // each picked perc → level 15
    itemStacksOverride: LUDICROUS_ITEM_STACKS, // each item → min(5, maxStacks)
  };
}

// BETA 17.92 — STRESS preset for 47-hr-equivalent runs.
let _stressCounter = 0;
export function randomizeStressBuild(): RandomBuild {
  const allWeapons = WEAPONS.map(w => w.id);
  const allItems = BUNCHA_BULLSHIIII.filter(i => i.status === 'active').map(i => i.id);
  const allShrines = SHRINE_BUFF_POOL.filter(b => b.rarity !== 'negative').map(b => b.id);
  const allPercs = PEANUT_PERCS.map(p => p.id);

  const weapons = _shuffle(allWeapons).slice(0, PICKS_PER_BUILD.weapons);
  const percs = _shuffle(allPercs).slice(0, STRESS_PERCS_PICKED);

  _stressCounter++;
  return {
    buildName: `STRESS #${_stressCounter}`,
    weapons,
    items:   allItems,              // every active item
    shrines: allShrines,            // EVERY shrine (not random 20)
    percs,                          // 4 random percs (slot cap)
    percLevel: STRESS_PERC_LEVEL,           // 100-level percs
    forceStacks: STRESS_ITEM_STACKS,        // BYPASS maxStacks — literal 100 of each
  };
}

/** Reset weapon-level runtime telemetry (totalDamageDealt, shotsFired, etc.)
 *  so the next build starts from zero. Called by applyBuild(). */
function _resetWeaponTelemetry(): void {
  const ws = getWeaponSystemState();
  if (!ws) return;
  for (const w of ws.weapons) {
    w.totalDamageDealt = 0;
    w.totalShotsFired = 0;
    w.totalShotsHit = 0;
    w.totalCrits = 0;
    w.totalCritRolls = 0;     // BETA 14.30
    w.totalTickEvents = 0;    // BETA 14.30
  }
}

/** Build the per-build CSV row: 10 columns matching the BETA 14.14 LabNote
 *  schema PLUS 3 prepended (buildName, buildSource, buildElapsedSec). */
function _buildRowSnapshot(row: BuildRow): string {
  const items = getEquippedItems();
  const enemies = getAliveEnemyCounts();
  const ws = getWeaponSystemState();
  const ps = getPercState();

  // BETA 14.30 (BUG-02) — weaponTelemetry format authoritative schema:
  //   `<id>:lvl<L>:dmg<D>:hits<H>/shots<S>:crits<C>:critRolls<CR>:tickEvents<TE>`
  // Where:
  //   H = totalShotsHit (target-hit events; can exceed S for piercing)
  //   S = totalShotsFired (projectile shots fired)
  //   C = totalCrits (target-hit events that were crit; can exceed shots for piercing)
  //   CR = totalCritRolls (BETA 14.30 BUG-01 — # of shots that rolled crit)
  //   TE = totalTickEvents (BETA 14.30 BUG-03/04 — orbital/aura/beam tick count)
  const weaponTelemetry = ws
    ? ws.weapons.map((w: WeaponInstance) => {
        const dmg = Math.round(w.totalDamageDealt ?? 0);
        return `${w.definitionId}:lvl${w.level}:dmg${dmg}:hits${w.totalShotsHit ?? 0}/${w.totalShotsFired ?? 0}:crits${w.totalCrits ?? 0}:critRolls${w.totalCritRolls ?? 0}:tickEvents${w.totalTickEvents ?? 0}`;
      }).join('|')
    : '';
  const percLevels = ps
    ? ps.slots.filter(s => s.perc && s.level > 0)
        .map(s => `${s.perc!.id}:lvl${s.level}`).join('|')
    : '';
  const groundCounts = ws
    ? (() => {
        const c: Record<string, number> = {};
        for (const ge of ws.groundEffects) c[ge.weaponId] = (c[ge.weaponId] ?? 0) + 1;
        return Object.entries(c).map(([id, n]) => `${id}:${n}`).join('|');
      })()
    : '';

  const elapsedSec = ((performance.now() - row.buildStartedAt) / 1000).toFixed(1);
  const escape = (s: string) => `"${String(s).replace(/"/g, '""')}"`;

  // BETA 14.30 (BUG-07) — shrineBuffs col is now JSON of {t, buff, stack}
  // events (was pipe-list of labels). For WeaponLab applyBuild() all shrines
  // are applied at t=0 relative to build start; for BotFarm runs the bot's
  // own per-pick logging populates real timestamps. Stack count derived from
  // duplicate shrine IDs in the build (e.g. picking +10% Damage twice = stack 2).
  const shrineCounts = new Map<string, number>();
  for (const sId of (row.shrines ?? [])) {
    shrineCounts.set(sId, (shrineCounts.get(sId) ?? 0) + 1);
  }
  const shrineBuffsArr: Array<{ t: number; buff: string; stack: number }> = [];
  shrineCounts.forEach((stack, sId) => {
    const b = SHRINE_BUFF_POOL.find(x => x.id === sId);
    shrineBuffsArr.push({ t: 0, buff: b?.label ?? sId, stack });
  });
  const shrineBuffs = JSON.stringify(shrineBuffsArr);

  // BETA 14.17 — death tracking columns. deaths = count, deathCauses = pipe
  // list of "cause@elapsedSec" pairs (relative to build start so the user can
  // see WHEN within the build they died and to WHAT).
  const deaths = getLabDeaths();
  const deathCount = deaths.length;
  const buildStartTimeSec = row.buildStartedAt / 1000;
  const deathCauses = deaths.map(d => {
    // d.atSec is state.time at death (game-clock seconds). buildStartedAt is
    // performance.now()/1000 — different clocks. Use deathCount-relative
    // index to keep the data simple: just emit cause + game-time-of-death.
    return `${d.cause}@${d.atSec.toFixed(1)}s`;
  }).join('|');
  void buildStartTimeSec;

  // BETA 14.27 — per-build combat-stat deltas. row.startCombatStats was
  // snapshotted at applyBuild() time; subtract from current to get per-build
  // values (the underlying state counters are run-cumulative, not per-build).
  const start: CombatStats = row.startCombatStats ?? {
    damageTaken: 0, attacksAttempted: 0, attacksHit: 0,
    attacksMissedByEvasion: 0, attacksMissedByIframe: 0, attacksMissedByPhase: 0,
    attacksMissedByOther: 0,
    reviveEvents: 0, healEvents: 0, dodgeEvents: 0, phaseEvents: 0, knockbackEvents: 0,
  };
  const damageTaken = Math.max(0, _cachedCombatStats.damageTaken - start.damageTaken);
  const attacksAttempted = Math.max(0, _cachedCombatStats.attacksAttempted - start.attacksAttempted);
  const attacksHit = Math.max(0, _cachedCombatStats.attacksHit - start.attacksHit);
  const attacksMissedByEvasion = Math.max(0, _cachedCombatStats.attacksMissedByEvasion - start.attacksMissedByEvasion);
  const attacksMissedByIframe = Math.max(0, _cachedCombatStats.attacksMissedByIframe - start.attacksMissedByIframe);
  const attacksMissedByPhase = Math.max(0, _cachedCombatStats.attacksMissedByPhase - start.attacksMissedByPhase);
  const attacksMissedByOther = Math.max(0, _cachedCombatStats.attacksMissedByOther - start.attacksMissedByOther);
  const reviveEvents = Math.max(0, _cachedCombatStats.reviveEvents - start.reviveEvents);
  const healEvents = Math.max(0, _cachedCombatStats.healEvents - start.healEvents);
  const dodgeEvents = Math.max(0, _cachedCombatStats.dodgeEvents - start.dodgeEvents);
  const phaseEvents = Math.max(0, _cachedCombatStats.phaseEvents - start.phaseEvents);
  const knockbackEvents = Math.max(0, _cachedCombatStats.knockbackEvents - start.knockbackEvents);

  return [
    escape(row.buildName),
    escape(row.buildSource),
    elapsedSec,
    escape(shrineBuffs),
    String(deathCount),
    escape(deathCauses),
    new Date().toISOString(),
    escape(getEquippedWeaponIds().join(',')),
    escape(items.map(i => `${i.id}:${i.stacks}`).join(',')),
    escape(Object.entries(enemies).map(([id, c]) => `${id}:${c}`).join(',')),
    escape(weaponTelemetry),
    escape(percLevels),
    escape(groundCounts),
    // BETA 14.27 — combat outcome columns (per-build deltas)
    String(Math.round(damageTaken)),
    String(attacksAttempted),
    String(attacksHit),
    String(attacksMissedByEvasion),
    String(attacksMissedByIframe),
    String(attacksMissedByPhase),
    // BETA 14.30 (BUG-05) — 5th outcome counter (per-build delta)
    String(attacksMissedByOther),
    // BETA 14.30 Phase 1 — event counters (per-build deltas)
    String(reviveEvents),
    String(healEvents),
    String(dodgeEvents),
    String(phaseEvents),
    String(knockbackEvents),
    // BETA 14.30 Phase 1 — stat snapshots (run-final, not per-build delta)
    String(_cachedStatSnapshot.finalDamagePct.toFixed(1)),
    String(_cachedStatSnapshot.finalCritChancePct.toFixed(1)),
    String(_cachedStatSnapshot.finalCritDamagePct.toFixed(1)),
    String(_cachedStatSnapshot.finalAttackSpeedPct.toFixed(1)),
    String(_cachedStatSnapshot.finalMovementSpeedPct.toFixed(1)),
    String(_cachedStatSnapshot.finalLifestealPct.toFixed(1)),
    String(_cachedStatSnapshot.finalEvasionPct.toFixed(1)),
    String(_cachedStatSnapshot.finalPhaseChancePct.toFixed(1)),
    String(_cachedStatSnapshot.finalPickupRangePct.toFixed(1)),
    String(_cachedStatSnapshot.finalLockOnRange.toFixed(1)),
    String(_cachedStatSnapshot.finalKnockbackPct.toFixed(1)),
    String(_cachedStatSnapshot.finalEliteDamagePct.toFixed(1)),
    String(_cachedStatSnapshot.finalThornsPct.toFixed(1)),
    String(_cachedStatSnapshot.finalXPGainPct.toFixed(1)),
    String(_cachedStatSnapshot.finalGoldGainPct.toFixed(1)),
    String(_cachedStatSnapshot.finalMaxHP.toFixed(0)),
    String(_cachedStatSnapshot.finalHPRegen.toFixed(2)),
    String(_cachedStatSnapshot.finalArmor.toFixed(0)),
    String(_cachedStatSnapshot.finalShield.toFixed(0)),
    String(_cachedStatSnapshot.finalOverheal.toFixed(0)),
    String(_cachedStatSnapshot.finalProjectileCount.toFixed(0)),
    String(_cachedStatSnapshot.finalProjectileBounce.toFixed(0)),
    String(_cachedStatSnapshot.finalProjectilePierce.toFixed(0)),
    // BETA 14.31 Phase 2 — per-perc / per-item / per-shrine trackers (JSON).
    escape(JSON.stringify(_localPercTracker)),
    escape(JSON.stringify(_localItemTracker)),
    escape(JSON.stringify(_localShrineTracker)),
    // BETA 14.32 Phase 3 — floor-effect tracker JSON + per-frame sweep cols + 9 Ugly Kitten cols
    escape(JSON.stringify(_localFloorEffectTracker)),
    String(_cachedSweepCounters.playerHpBucketLowFrames),
    String(_cachedSweepCounters.playerHpBucketMidFrames),
    String(_cachedSweepCounters.playerHpBucketHighFrames),
    String(_cachedSweepCounters.playerStationaryFrames),
    String(_cachedSweepCounters.playerDiagonalMoveFrames),
    String(_cachedSweepCounters.noEnemyNearbyFrames),
    String(_cachedSweepCounters.fivePlusEnemiesIn150wuFrames),
    String(_cachedSweepCounters.playerMSAvg.toFixed(1)),
    String(_cachedSweepCounters.phaseChanceCurrentAvg.toFixed(2)),
    String(_cachedSweepCounters.critChanceCurrentAvg.toFixed(2)),
    // BETA 14.54/14.56 — prefer _local counters (populated by weaponSystem
    // tracker callbacks) over _cachedSweepCounters (wired to bot fields, but
    // those were never incremented). Math.max ensures whichever is non-zero
    // wins. 14.56 expanded to cover kills + lifetime + coord buckets.
    String(Math.max(_cachedSweepCounters.kittenPlacementsCount, _localKittenPlacements)),
    String(_cachedSweepCounters.kittenActiveAtAnyTimeAvg.toFixed(1)),
    String(Math.max(_cachedSweepCounters.kittenAttacksFiredTotal, _localKittenAttacks)),
    String(Math.max(_cachedSweepCounters.kittenDmgDealtTotal, _localKittenDmg)),
    String(Math.max(_cachedSweepCounters.kittenKillsTotal, _localKittenKills)),
    String(_cachedSweepCounters.kittenFreezeAppliedFrames),
    // BETA 14.56 — Lifetime running avg from observed kitten projectile deaths
    String((_localKittenLifetimeCount > 0 ? _localKittenLifetimesMs / _localKittenLifetimeCount : _cachedSweepCounters.kittenLifetimeAvgMs).toFixed(0)),
    // Death rate: kittensDied / kittensPlaced (0..1). 0 = none died yet.
    String(_localKittenPlacements > 0 ? (_localKittenLifetimeCount / _localKittenPlacements).toFixed(2) : String(_cachedSweepCounters.kittenDeathRate)),
    String(Math.max(_cachedSweepCounters.kittenPlacementCornerCount, _localKittenCornerCount)),
    String(Math.max(_cachedSweepCounters.kittenPlacementCenterCount, _localKittenCenterCount)),
    String(Math.max(_cachedSweepCounters.kittenPlacementScatterCount, _localKittenScatterCount)),
    // BETA 14.33 Phase 4 — weapon × enemy matrix + 5 top-line cols + synergy tracker
    escape(JSON.stringify(_localWeaponEnemyMatrix)),
    (() => {
      const top = computeTopWeaponSummary();
      _phase4_topVsZombie = top.topVsZombie;
      _phase4_topVsTimmy = top.topVsTimmy;
      _phase4_topVsCaseoh = top.topVsCaseoh;
      _phase4_topVsElite = top.topVsElite;
      _phase4_weakest = top.weakest;
      return escape(_phase4_topVsZombie);
    })(),
    escape(_phase4_topVsTimmy),
    escape(_phase4_topVsCaseoh),
    escape(_phase4_topVsElite),
    escape(_phase4_weakest),
    (() => {
      // Refresh synergy bothEquipped flags from current equipment
      const equipped = new Set<string>();
      getEquippedWeaponIds().forEach(w => equipped.add(w));
      getEquippedItems().forEach(i => equipped.add(i.id));
      const ps2 = getPercState();
      ps2?.slots.forEach(s => { if (s.perc) equipped.add(s.perc.id); });
      refreshSynergyTracker(equipped);
      return escape(JSON.stringify(_localSynergyTracker));
    })(),
    // BETA 14.34 Phase 5 — event log + death context + near-miss + timing + perf
    escape(JSON.stringify(_localEventLog)),
    escape(JSON.stringify(_localDeathContext)),
    String(_localNearMissEvents.length),
    escape(JSON.stringify(_localNearMissEvents)),
    String(_localFirstBloodTime.toFixed(2)),
    String(_localFirstDamageTakenTime.toFixed(2)),
    String(_localLongestStretchBetweenHitsMs.toFixed(0)),
    escape(JSON.stringify(_cachedFramePerf)),
    // BETA 14.36 — retreat / warp event log. Each entry is one peel-off →
    // recycle event with type, retreat-start position, warp-end position,
    // distance from player, and (if reached) time to re-contact.
    String(_localRetreatEvents.length),
    escape(JSON.stringify(_localRetreatEvents)),
  ].join(',');
}

// BETA 14.33 — module-scope cache for top weapon summary so we can compute
// once and emit the values across multiple cols in the row emit chain.
let _phase4_topVsZombie = '';
let _phase4_topVsTimmy = '';
let _phase4_topVsCaseoh = '';
let _phase4_topVsElite = '';
let _phase4_weakest = '';

/** BETA 14.16 — MAX-build apply. Finalize previous row, clear current
 *  loadout + shrines + percs, apply new (4 weapons + 10 items + 15 shrines +
 *  4 percs at level 6), reset weapon-level telemetry, start new row. */
export function applyBuild(args: {
  buildName: string;
  buildSource: BuildRow['buildSource'];
  weapons: string[];
  items: string[];
  shrines?: string[];
  percs?: string[];
  percLevel?: number;
  // BETA 17.86 — LUDICROUS specifies "5 of each item" via this override.
  // When set, each item stacks at min(itemStacksOverride, item.maxStacks).
  // When unset (MAX / preset paths), each item stacks at its maxStacks
  // (the BETA 14.16 default). Non-stackable items always = 1 stack.
  itemStacksOverride?: number;
  // BETA 17.93 — STRESS: literal stack count, BYPASSES per-item maxStacks.
  // Non-stackable items still = 1.
  forceStacks?: number;
}): void {
  // 1. Finalize previous row (snapshots current deaths into the row before
  //    we clear the log for the new build)
  if (_activeBuildRow) {
    _activeBuildRow.finalizedRow = _buildRowSnapshot(_activeBuildRow);
    _sessionBuildRows.push(_activeBuildRow);
    _activeBuildRow = null;
  }
  // BETA 14.17 — clear lab death log so the new build's deaths column only
  // counts deaths that happen during this build's window.
  clearLabDeaths();
  // BETA 14.31 Phase 2 — clear local trackers so each build's tracker JSONs
  // reflect ONLY mechanics that fired during this build's window.
  clearTrackers();
  // BETA 14.45 — request engine-side per-build state reset (next frame).
  // Fixes the RANDOMIZE leak (9755 HP / 25888% damage by 11th randomize).
  requestPerBuildStateReset();
  // BETA 17.80 — Dylan: "on each running build all enemies reset." Clear
  // every enemy in the arena before applying the new build so each build's
  // telemetry starts from a clean slate. Without this, enemies that were
  // alive at the moment of build-rotation carried over and contaminated
  // the new build's first 30-60 sec of kill data.
  clearAllEnemies();
  // 2. Clear current loadout (weapons, items, shrines, percs)
  for (const wId of getEquippedWeaponIds()) setWeaponEquipped(wId, false);
  for (const it of getEquippedItems()) setItemStacks(it.id, 0);
  clearAppliedShrineBuffs();
  // Clear all percs from slots (cap by maxPercSlots — typically 4)
  const ps = getPercState();
  if (ps) {
    for (const slot of ps.slots) {
      if (slot.perc) {
        slot.perc = null;
        slot.level = 0;
        slot.accumulatedBonus = 0;
      }
    }
  }

  // 3. Apply weapons
  for (const wId of args.weapons) setWeaponEquipped(wId, true);
  // 4. Apply items. Default: maxStacks (BETA 14.16). With itemStacksOverride
  //    (BETA 17.86 LUDICROUS): each item stacks at min(override, maxStacks).
  //    With forceStacks (BETA 17.93 STRESS): bypasses maxStacks entirely.
  for (const iId of args.items) {
    const def = BUNCHA_BULLSHIIII.find(b => b.id === iId);
    if (!def?.stackable) { setItemStacks(iId, 1); continue; }
    const defMax = def.maxStacks ?? 3;
    const stacks = args.forceStacks != null
      ? args.forceStacks                                   // STRESS: literal, ignores maxStacks
      : args.itemStacksOverride != null
        ? Math.min(args.itemStacksOverride, defMax)        // LUDICROUS: capped
        : defMax;                                          // MAX: each item's maxStacks
    setItemStacks(iId, stacks);
  }
  // 5. Apply shrine buffs (push to shrineAppliedBuffs)
  if (args.shrines) {
    for (const sId of args.shrines) {
      applyShrineBuffById(sId);
      // BETA 14.31 Phase 2 — track shrine apply. Use the buff's value as
      // the delta (numerical magnitude — analyst sees stacking deltas).
      const buff = SHRINE_BUFF_POOL.find(b => b.id === sId);
      const delta = (buff?.value ?? 0) * (buff?.pct ? 1 : 1); // raw value; PCT-flag info kept in shrine label
      trackShrineApply(buff?.label ?? sId, delta);
    }
  }
  // 6. Apply perc levels (incrementPercLevel up to target level — fills empty
  //    slots first, then increments). percLevel default = 6 per spec.
  if (args.percs) {
    const targetLevel = args.percLevel ?? PICKS_PER_BUILD.percLevel;
    for (const percId of args.percs) {
      for (let i = 0; i < targetLevel; i++) {
        incrementPercLevel(percId);
      }
    }
  }

  // 7. Reset weapon-level telemetry counters
  _resetWeaponTelemetry();
  // 8. Start new row + snapshot combat stat starting values (BETA 14.27)
  _activeBuildRow = {
    buildName: args.buildName,
    buildSource: args.buildSource,
    buildStartedAt: performance.now(),
    shrines: args.shrines,
    percs: args.percs,
    percLevel: args.percLevel ?? PICKS_PER_BUILD.percLevel,
    startCombatStats: { ..._cachedCombatStats },
  };
}

/** Returns the current session size: finalized rows + 1 in-progress (if any). */
export function getSessionRowCount(): { finalized: number; inProgress: boolean } {
  return { finalized: _sessionBuildRows.length, inProgress: _activeBuildRow != null };
}

/** Download the multi-build CSV. Finalizes the in-progress row first so the
 *  user gets latest data in the dump. */
export function downloadMultiBuildCsv(): void {
  // Finalize in-progress row
  if (_activeBuildRow) {
    _activeBuildRow.finalizedRow = _buildRowSnapshot(_activeBuildRow);
    _sessionBuildRows.push(_activeBuildRow);
    _activeBuildRow = null;
  }
  const header = [
    'buildName', 'buildSource', 'buildElapsedSec', 'shrineBuffs',
    'deaths', 'deathCauses',
    'timestamp', 'weapons', 'items', 'enemies',
    'weaponTelemetry', 'percLevels', 'groundEffectCounts',
    // BETA 14.27 — combat outcome columns. Per-build deltas of state's run
    // counters. Use these to verify defensive procs / contact rates per build.
    'damageTaken', 'enemyAttacksAttempted', 'enemyAttacksHit',
    'enemyAttacksMissedByEvasion', 'enemyAttacksMissedByIframe',
    'enemyAttacksMissedByPhase',
    // BETA 14.30 (BUG-05) — 5th outcome counter (shield-fully-absorbed etc.)
    'enemyAttacksMissedByOther',
    // BETA 14.30 Phase 1 — event counters (per-build deltas)
    'reviveEvents', 'healEvents', 'dodgeEvents', 'phaseEvents', 'knockbackEvents',
    // BETA 14.30 Phase 1 — stat snapshots (run-final values)
    'finalDamagePct', 'finalCritChancePct', 'finalCritDamagePct',
    'finalAttackSpeedPct', 'finalMovementSpeedPct', 'finalLifestealPct',
    'finalEvasionPct', 'finalPhaseChancePct', 'finalPickupRangePct',
    'finalLockOnRange', 'finalKnockbackPct', 'finalEliteDamagePct',
    'finalThornsPct', 'finalXPGainPct', 'finalGoldGainPct',
    'finalMaxHP', 'finalHPRegen', 'finalArmor', 'finalShield',
    'finalOverheal', 'finalProjectileCount', 'finalProjectileBounce',
    'finalProjectilePierce',
    // BETA 14.31 Phase 2 — per-perc / per-item / per-shrine trackers (JSON cols)
    'percTracker', 'itemTracker', 'shrineTracker',
    // BETA 14.32 Phase 3 — floor-effect tracker JSON + 10 sweep cols + 9 kitten cols + 3 kitten placement buckets
    'floorEffectTracker',
    'playerHpBucketLowFrames', 'playerHpBucketMidFrames', 'playerHpBucketHighFrames',
    'playerStationaryFrames', 'playerDiagonalMoveFrames',
    'noEnemyNearbyFrames', '5plusEnemiesIn150wuFrames',
    'playerMSAvg', 'phaseChanceCurrentAvg', 'critChanceCurrentAvg',
    'kittenPlacementsCount', 'kittenActiveAtAnyTimeAvg', 'kittenAttacksFiredTotal',
    'kittenDmgDealtTotal', 'kittenKillsTotal', 'kittenFreezeAppliedFrames',
    'kittenLifetimeAvgMs', 'kittenDeathRate',
    'kittenPlacementCornerCount', 'kittenPlacementCenterCount', 'kittenPlacementScatterCount',
    // BETA 14.33 Phase 4 — weapon × enemy matrix + 5 top-line cols + synergy tracker
    'weaponEnemyMatrix',
    'topWeaponVsZombie', 'topWeaponVsTimmy', 'topWeaponVsCaseoh', 'topWeaponVsElite',
    'weakestWeaponOverall',
    'synergyTracker',
    // BETA 14.34 Phase 5 — event log + death context + near-miss + timing + perf
    'eventLog', 'deathContext',
    'nearMissEventsCount', 'nearMissEventsLog',
    'firstBloodTime', 'firstDamageTakenTime', 'longestStretchBetweenHitsMs',
    'engineFramePerf',
    // BETA 14.36 — retreat / warp event log
    'retreatEventsCount', 'retreatEventsLog',
  ].join(',');
  const rows = _sessionBuildRows.map(r => r.finalizedRow ?? '').filter(Boolean);
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `weaponlab_session_${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/** BETA 14.18 — Wipe just the LOADOUT (weapons + items + shrines + percs).
 *  Distinct from clearSession() which also clears the CSV rows + round-robin
 *  pools. Use this when you want to hand-pick a clean slate without losing
 *  accumulated build snapshots. */
export function clearLoadout(): void {
  for (const w of WEAPONS) setWeaponEquipped(w.id, false);
  for (const i of BUNCHA_BULLSHIIII) setItemStacks(i.id, 0);
  clearAppliedShrineBuffs();
  const ps = getPercState();
  if (ps) {
    for (const slot of ps.slots) {
      if (slot.perc) {
        slot.perc = null;
        slot.level = 0;
        slot.accumulatedBonus = 0;
      }
    }
  }
}

/** Clear all session rows + reset round-robin pools + reset telemetry +
 *  clear shrines/percs/deaths. */
export function clearSession(): void {
  _sessionBuildRows.length = 0;
  _activeBuildRow = null;
  _seenWeaponsThisSession.clear();
  _seenItemsThisSession.clear();
  _seenShrinesThisSession.clear();
  _seenPercsThisSession.clear();
  _randomBuildCounter = 0;
  _resetWeaponTelemetry();
  clearAppliedShrineBuffs();
  clearLabDeaths();
}

// ─── Pre-built named loadouts ────────────────────────────────────────────────
// 34 presets covering character defaults, build archetypes, scaling-disconnect
// verification, BETA 14.04 feature smoke tests, and synergy combos.
export interface PresetBuild {
  id: string;
  name: string;
  category: 'character' | 'archetype' | 'scaling-test' | 'feature-test' | 'synergy';
  weapons: string[];
  items: string[];
  /** BETA 14.16 — optional shrine + perc themes (MAX-build presets). When
   *  omitted, applyBuild() applies just weapons + items (legacy behavior). */
  shrines?: string[];
  percs?: string[];
  percLevel?: number;
}

export const PRESET_BUILDS: PresetBuild[] = [
  // Character defaults (10)
  { id: 'COWBOY_DEFAULT',   name: 'Cowboy Default',   category: 'character', weapons: ['revolver'], items: [] },
  { id: 'KILLA_DEFAULT',    name: 'Killa Default',    category: 'character', weapons: ['rpk_machine_gun'], items: [] },
  { id: 'VIKING_DEFAULT',   name: 'Viking Default',   category: 'character', weapons: ['viking_axe'], items: [] },
  { id: 'TACTICAL_DEFAULT', name: 'Tactical Default', category: 'character', weapons: ['peanut_rifle'], items: [] },
  { id: 'LEBRON_DEFAULT',   name: 'Lebron Default',   category: 'character', weapons: ['tactical_basketball'], items: [] },
  { id: 'WIZARD_DEFAULT',   name: 'Wizard Default',   category: 'character', weapons: ['fireball_wand'], items: [] },
  { id: 'MR_SILLY_DEFAULT', name: 'Mr Silly Default', category: 'character', weapons: ['voogoo_rager_toss'], items: [] },
  { id: 'SPONGE_DEFAULT',   name: 'Sponge Default',   category: 'character', weapons: ['spatula_slash'], items: [] },
  { id: 'PIRATE_DEFAULT',   name: 'Pirate Default',   category: 'character', weapons: ['blunderbuss'], items: [] },
  { id: 'CHAD_DEFAULT',     name: 'Chad Default',     category: 'character', weapons: ['chad_aura'], items: [] },
  // Build archetypes (7) — BETA 17.75: stale item_* IDs replaced with current
  // master-DB short codes (C02/C04/C05/C06/C08/UC02/UC03/C10/R02/R07). Plus
  // `wpn_iranian_bird` was incorrectly listed as a weapon (it's an item:
  // `item_iranian_bird`) — moved to items. Plus `wpn_blunderbuss` → `blunderbuss`.
  // Pre-17.75: preset items silently failed to load. Lab testing was on bare
  // weapon-only builds even when preset said items should be equipped.
  { id: 'CHAD_AURA_FULL',     name: 'Chad Aura (full)',     category: 'archetype', weapons: ['chad_aura', 'wpn_ultra_beam', 'wpn_karen_drones', 'wpn_dracs_fang'], items: ['C02', 'item_bungulators'] },
  { id: 'PEANUT_SNIPER_FULL', name: 'Peanut Sniper (full)', category: 'archetype', weapons: ['peanut_rifle', 'wpn_frozen_darts', 'wpn_ex_girlfriend', 'wpn_the_terminator'], items: ['C05', 'C04'] },
  { id: 'RPK_SUPPRESSOR',     name: 'RPK Suppressor',       category: 'archetype', weapons: ['rpk_machine_gun', 'wpn_squirter', 'wpn_hurray'], items: ['item_iranian_bird', 'C08', 'R07'] },
  { id: 'SPATULA_MELEE_FULL', name: 'Spatula Melee (full)', category: 'archetype', weapons: ['spatula_slash', 'wpn_dracs_fang', 'wpn_ugly_kittens', 'wpn_poop_sling'], items: ['item_dracs_gas_mask', 'item_bungulators'] },
  { id: 'FIREBALL_KITE_FULL', name: 'Fireball Kite (full)', category: 'archetype', weapons: ['fireball_wand', 'wpn_frozen_darts', 'wpn_shitty_cannon', 'wpn_poop_sling'], items: ['item_shit', 'item_asmons_desk_banana', 'item_stay_frosty'] },
  { id: 'TANK_SUSTAIN',       name: 'Tank Sustain (revives)', category: 'archetype', weapons: ['blunderbuss', 'wpn_dracs_fang', 'wpn_ugly_kittens'], items: ['item_sheeesh', 'item_liar_deceiver_false_prophet', 'item_caseoh_moms_cooking', 'item_bungulators'] },
  { id: 'HOLY_TRINITY',       name: 'Holy Trinity (xp/gold)', category: 'archetype', weapons: ['peanut_rifle', 'wpn_ultra_beam'], items: ['C05', 'C04', 'R02'] },
  // Scaling-disconnect tests (5)
  { id: 'BUNGULATORS_DAMAGE_SCALING',  name: 'Bungulators × Damage Scaling',  category: 'scaling-test', weapons: ['revolver'],          items: ['item_bungulators', 'C08', 'UC02'] },
  { id: 'IRANIAN_BIRD_SPEED_SCALING',  name: 'Iranian Bird × Attack Speed',   category: 'scaling-test', weapons: ['rpk_machine_gun'],   items: ['item_iranian_bird', 'C08', 'R07'] },
  { id: 'BANANA_TRAP_SCALING',         name: 'Banana × Damage Scaling',       category: 'scaling-test', weapons: ['revolver'],          items: ['item_asmons_desk_banana', 'C08', 'UC02'] },
  { id: 'BIG_MAC_DPS_SCALING',         name: 'Big Mac DoT × Damage Scaling',  category: 'scaling-test', weapons: ['rpk_machine_gun'],   items: ['item_big_mac_special_sauce', 'C08', 'UC02'] },
  { id: 'MOMS_COOKING_SCALING',        name: 'Mom\'s Cooking × Heal Items',   category: 'scaling-test', weapons: ['revolver'],          items: ['item_caseoh_moms_cooking', 'C10', 'UC03'] },
  // BETA 14.04 feature smoke tests (7)
  { id: 'BIG_MAC_BURN_TEST',           name: 'Big Mac Burn DoT',              category: 'feature-test', weapons: ['rpk_machine_gun', 'wpn_squirter'], items: ['item_big_mac_special_sauce'] },
  { id: 'STAY_FROSTY_SLOW_TEST',       name: 'Stay Frosty On-Hit Slow',       category: 'feature-test', weapons: ['rpk_machine_gun', 'wpn_squirter'], items: ['item_stay_frosty'] },
  { id: 'MOMS_COOKING_HEAL_TEST',      name: 'Mom\'s Cooking Heal-on-Kill',   category: 'feature-test', weapons: ['rpk_machine_gun', 'blunderbuss'], items: ['item_caseoh_moms_cooking'] },
  { id: 'MYSTERY_MEAT_RANDOM_TEST',    name: 'Mystery Meat Random Stats',     category: 'feature-test', weapons: ['rpk_machine_gun'],   items: ['item_mystery_meat_stew'] },
  { id: 'DOUBLE_REVIVE_TEST',          name: 'Sheeesh + Liar Double-Revive',  category: 'feature-test', weapons: ['revolver'],          items: ['item_sheeesh', 'item_liar_deceiver_false_prophet'] },
  { id: 'WRITE_THAT_DOWN_TEST',        name: 'Write That Down (XP boost)',    category: 'feature-test', weapons: ['rpk_machine_gun'],   items: ['item_write_that_down'] },
  { id: 'TIMMY_REPELLENT_TEST',        name: 'Bully Spray (vs Brawlers)',     category: 'feature-test', weapons: ['rpk_machine_gun'],   items: ['item_timmy_repellent_spray'] },
  // Synergy combos (5)
  { id: 'DRACS_VAMPIRE',  name: "Drac's Vampire (lifesteal 20%)",  category: 'synergy', weapons: ['wpn_dracs_fang'],                   items: ['item_dracs_gas_mask'] },
  { id: 'GLASS_CANNON',   name: 'Glass Cannon (low-HP scaling)',    category: 'synergy', weapons: ['rpk_machine_gun', 'wpn_ultra_beam'], items: [] },
  { id: 'ARMORED_SWARM',  name: 'Armored Swarm (Hutch + Bungula)',  category: 'synergy', weapons: ['spatula_slash', 'wpn_dracs_fang'], items: ['C06', 'item_bungulators'] },
  { id: 'GOLD_ECONOMY',   name: 'Gold Economy (greed scaling)',     category: 'synergy', weapons: ['rpk_machine_gun'],                  items: ['R02', 'C05'] },
  { id: 'ANTI_SNOWBALL',  name: 'Anti-Snowball (early/late scale)', category: 'synergy', weapons: ['rpk_machine_gun', 'wpn_ultra_beam'], items: [] },
];
